/**
 * Application entry point: the map, what a click means, and — since Phase 12 — how many maps
 * there are (P9.4, P9.5, P9.6, P11.3, P12.1).
 *
 * **This module owns interaction mode; `renderer.ts` owns where the click landed** (§8). That
 * split is the escape hatch the renderer boundary exists to keep open — a renderer that knew about
 * modes would have to be reimplemented by any replacement.
 *
 * ## Inspect is the default, and it issues no request (`V14`, §3.0)
 *
 * A click in inspect mode resolves to the nearest **rendered** point and reads that row's stored
 * sequence out of the tile datum. No latent vector is built, no `(D,)` array is invented, and
 * **no request reaches the server** — the sequence is in the tile (`V13`) and the hit test is a
 * one-pixel GPU read. `web/test/app.browser.test.ts` asserts the network stays silent, which is
 * §9's row *"Inspect mode issues no request"*.
 *
 * Inspect is the default because it is the honest one: everything it shows is a real sequence that
 * was really in the corpus, at a position that is really its own. **Generating a new sequence is
 * something the user asks for by picking up a tool, never something a stray click does.**
 *
 * ## The two things that do reach the server
 *
 * - **"show reconstruction"** on a selected point — `POST /decode` with `row` (P9.6). The one
 *   inspect-mode action that is a request, taken deliberately, and its answer appears *beside* the
 *   stored sequence rather than in place of it.
 * - **the decoder tool** — `POST /decode` with `xy`, the projection, the fill and the sampler
 *   (P9.5).
 *
 * A raw-dimension view adds one more, and it is a *view* rather than an interaction: `GET
 * /rawdims` fetches the untiled Arrow buffer for a pair, because `C(D, 2)` pairs cannot be
 * pre-tiled (`V12`). Once it is loaded, a click on it costs exactly what a click on a tiled view
 * costs, which is nothing.
 *
 * **`datum.row`, never `datum.ix`** (`V51`). A tile datum carries both; `ix` is deepscatter's
 * rendering rank and on a subsampled view it names a different peptide.
 *
 * ## Two panes, and why the state is per pane (`V17`, P12.1)
 *
 * IVELS's framing is that comparing models *is* the task, which is why `V17` is side-by-side
 * rather than a dropdown. So everything that describes *one map* — its renderer, its controls, its
 * detail pane, its health panel, its status line — lives on a `Pane`, and this module holds a list
 * of them. What is genuinely global is small and named as such: the atlas listing, whether we are
 * comparing, whether channel changes propagate, and the eviction notice.
 */
import {
  ApiError,
  Unauthenticated,
  decode,
  encode,
  interpolate,
  isPeptideAtlas,
  latentStats,
  listAtlases,
  loadAtlas,
  login,
  parseRawProjection,
  rawDims,
  rawProjectionName,
  residueCodes,
  curatedLists,
  encodeBatch,
  tilesUrl,
  DEFAULT_STEPS,
  type AtlasListing,
  type AtlasManifest,
  type CuratedSet,
  type DecodeResponse,
  type InterpolationStep,
  type LatentRef,
  type ProjectionSpec,
} from './api';
import {
  createDecodedStore,
  type DecodedMark,
  type DecodedView,
  type InterpolationRun,
} from './decoded';
import { CAP_PER_VIEW, createEncodedStore, type EncodedMark } from './encoded';
import {
  createCuratedCache,
  neededSequences,
  resolveGlyphs,
  hasLandmarks,
  type CuratedView,
} from './curated';
import { createControls, type ControlState, type Controls, type Dataset } from './panels/controls';
import {
  createDetail,
  decodedMark,
  encodedMark,
  pinnedPointCard,
  type CardNotes,
  type Detail,
} from './panels/detail';
import {
  createEncoderPanel,
  type EncoderHandlers,
  type EncoderPanel,
  type ParsedRow,
} from './panels/encoder';
import {
  createInterpolatePanel,
  type Endpoint,
  type InterpolateHandlers,
  type InterpolatePanel,
  type PlacedStep,
} from './panels/interpolate';
import {
  evictionForecast,
  latentRefOf,
  flatten,
  newRunId,
  placeable,
  runPosition,
  spanOf,
} from './interpolation';
import { createResidueCards } from './panels/residues';
import {
  createTrajectoryPanel,
  type TrajectoryHandlers,
  type TrajectoryPanel,
} from './panels/trajectory';
import {
  append as appendPoint,
  encodedKey,
  encodedPoint,
  generatedPoint,
  indexOfKey,
  keyOf,
  markKey,
  moveBy,
  originOf,
  removeAt,
  rowKey,
  steps,
  storedPoint,
  withoutKeys,
  type Baseline,
  type EntryStatus,
  type Step,
  type TrajectoryPoint,
} from './trajectory';
import { createPanelResizer } from './panels/resize';
import { createHealth, type Health, type LatentStatsPayload } from './panels/health';
import { createRenderer, type LatentRenderer, type PointDatum } from './renderer';

export const APP_ROOT_ID = 'app';

const maps = document.querySelector('#maps') as HTMLElement;
const panel = document.querySelector('#panel') as HTMLElement;
const globalRoot = document.querySelector('#global') as HTMLElement;
const statusbar = document.querySelector('#statusbar') as HTMLElement;
const noticeRoot = document.querySelector('#notice') as HTMLElement;
const gate = document.querySelector('#login') as HTMLElement;
const resizer = document.querySelector('#panel-resizer') as HTMLElement | null;
const clearPinsButton = document.querySelector('#clear-pins') as HTMLButtonElement | null;
clearPinsButton?.addEventListener('click', () => {
  // **The decoded and typed cards go too** (`V129`, P25, extended at P29). The reader's phrasing
  // was that clearing the cards should include the sequence cards of decoded points, and a control
  // that left some cards on a covered map does not do what its label says. The glyphs stay: this
  // closes, never deletes — for either provenance.
  //
  // **First, not last** (`V132`): closing one of those cards changes that entry's `EntryStatus`
  // from `placed` to `placeable`, and `clearPin` is what re-renders the panel. The other order
  // left the panel offering no `place` control for a card it had just closed.
  closeDecodedCards();
  closeEncodedCards();
  for (const pane of panes) clearPin(pane);
});

/**
 * Everything a decode leaves behind, for every atlas this browser has looked at (`V123`, P20).
 *
 * Module-level and not per pane, because a mark belongs to a *view* rather than to a half of the
 * screen: two panes on the same atlas and the same projection are looking at the same coordinates,
 * and a point the model generated there is equally a fact about both.
 */
const decodedStore = createDecodedStore();

const clearDecodedButton = document.querySelector('#clear-decoded') as HTMLButtonElement | null;

/**
 * How long "clear all" stays armed after the first click (`V123`, P20.7).
 *
 * **Two-step here and one-step for the right-click, and the asymmetry is deliberate.** Neither
 * gesture can be undone — a sampled sequence is not reproducible — but a mis-aimed right click
 * costs one point while a mis-aimed clear costs every point on screen. The confirmation is priced
 * against what is lost, not against how destructive the verb sounds.
 */
const CLEAR_CONFIRM_MS = 3000;

/** The pending disarm, so the button does not stay armed after the reader has moved on. */
let clearDecodedArmed: ReturnType<typeof setTimeout> | null = null;

function disarmClearDecoded(): void {
  if (clearDecodedArmed !== null) clearTimeout(clearDecodedArmed);
  clearDecodedArmed = null;
}

clearDecodedButton?.addEventListener('click', () => {
  if (clearDecodedArmed !== null) {
    disarmClearDecoded();
    // **Only what is on screen**, which is what the label counted. `removeMany` over the visible
    // ids rather than emptying the key, so marks belonging to an atlas the reader is not looking
    // at survive a gesture that never named them.
    const deleted = panes.flatMap((pane) => [...pane.marks.keys()]);
    decodedStore.removeMany(deleted);
    for (const pane of panes) clearMarks(pane);
    // **The same rule `removeMark` follows** (`V133`): these points are gone, so no walk may go on
    // naming them. The ids are collected before `clearMarks` empties `pane.marks`, which is where
    // they are read from.
    dropFromWalks(new Set(deleted.map(markKey)));
    renderMapActions();
    // The run these points belonged to no longer has points, and the forecast has moved.
    for (const pane of panes) {
      pane.interpolation = { ...pane.interpolation, result: null, placed: [], runId: null };
      renderInterpolate(pane);
    }
    return;
  }
  clearDecodedArmed = setTimeout(() => {
    clearDecodedArmed = null;
    renderMapActions();
  }, CLEAR_CONFIRM_MS);
  renderMapActions();
});

/**
 * Every sequence the reader has typed and placed, for every atlas this browser has looked at
 * (`V137`-`V139`, P29).
 *
 * Module-level for `decodedStore`'s reason: a placed sequence belongs to a *view* rather than to a
 * half of the screen. Two panes on the same atlas and projection are looking at the same
 * coordinates, and where this model puts a given peptide is equally a fact about both.
 */
const encodedStore = createEncodedStore();

/**
 * The cards whose grey notes are hidden right now, by `keyOf` key (`V148`).
 *
 * **Module-level and not on the record**, which is the same split `pane.pins` keeps: a card's
 * *content* is a fact about the point and is stored, while how the reader has arranged the map —
 * where a card was dragged to, how wide it was pulled — is a property of this session and is not.
 * The notes switch is that second kind. It is deliberately **not** persisted: a reader returning
 * to a reload should be told what every card is, and the default is the informative one.
 *
 * Held here rather than per pane for `decodedStore`'s reason: two panes showing the same point are
 * showing the same card, and a switch that applied to one of them would be a card that says two
 * different things about one peptide.
 */
const terseCards = new Set<string>();

/**
 * The notes switch for one card: whether it is terse, and what a click on it redraws.
 *
 * `redraw` is passed in because the three kinds of card are drawn by three different functions,
 * and each of them already knows how to rebuild every pane that holds it — which is what has to
 * happen, since the builders are pure and the node is the whole of the update.
 */
function cardNotes(key: string, redraw: () => void): CardNotes {
  return {
    terse: terseCards.has(key),
    toggle: () => {
      if (terseCards.has(key)) terseCards.delete(key);
      else terseCards.add(key);
      redraw();
    },
  };
}

const clearEncodedButton = document.querySelector('#clear-encoded') as HTMLButtonElement | null;

let clearEncodedArmed: ReturnType<typeof setTimeout> | null = null;

function disarmClearEncoded(): void {
  if (clearEncodedArmed !== null) clearTimeout(clearEncodedArmed);
  clearEncodedArmed = null;
}

clearEncodedButton?.addEventListener('click', () => {
  if (clearEncodedArmed !== null) {
    disarmClearEncoded();
    // Only what is on screen, exactly as `#clear-decoded` has it: `removeMany` over the visible
    // ids rather than emptying the key, so points belonging to an atlas the reader is not looking
    // at survive a gesture that never named them.
    //
    // **Stale records are deliberately not swept up here** (`V139`). They are not on screen and
    // the label did not count them, and they are the one thing in this store that a reload cannot
    // reproduce — the sequences are the reader's own. Emptying them out under a control labelled
    // with a different number is exactly the silent loss this store exists to prevent.
    const deleted = panes.flatMap((pane) => [...pane.encoded.keys()]);
    encodedStore.removeMany(deleted);
    for (const pane of panes) clearEncodedMarks(pane);
    dropFromWalks(new Set(deleted.map(encodedKey)));
    renderMapActions();
    for (const pane of panes) renderEncoder(pane);
    return;
  }
  clearEncodedArmed = setTimeout(() => {
    clearEncodedArmed = null;
    renderMapActions();
  }, CLEAR_CONFIRM_MS);
  renderMapActions();
});

/**
 * Every residue code the depiction library can draw, fetched once and kept for the session.
 *
 * **Cached by nothing, because there is nothing to key it by that can change while the page is
 * open.** The bundle is a build artefact of one library revision; `GET /residues` returns that
 * revision beside the codes, and a server cannot swap bundles without restarting. One fetch.
 *
 * **Fetched lazily, when a composer first renders**, not at boot. It is needed only by a panel the
 * reader may never open, and the app must draw maps on an installation that has no depictions at
 * all — where this route is a 409 and the composer degrades to free text (`api.residueCodes`).
 */
let residueCodeList: readonly string[] = [];
let residueCodesRequested = false;

function ensureResidueCodes(): void {
  if (residueCodesRequested) return;
  residueCodesRequested = true;
  void residueCodes()
    .then((answer) => {
      residueCodeList = answer.codes;
      for (const pane of panes) renderEncoder(pane);
    })
    .catch(() => {
      // A 409 (no bundle built) or a network failure. **Not surfaced as an error**: the composer
      // works without completions, and the maps are documented to work without depictions. What
      // the reader would see otherwise is a failure notice for a feature that is functioning.
    });
}

/**
 * The curated peptide lists, fetched once at boot (`V150`, P31).
 *
 * **Eagerly, unlike `ensureResidueCodes`**, and the difference is what the answer controls. The
 * residue codes feed completions inside a panel the reader may never open, so fetching them lazily
 * costs nothing. These decide whether three *controls exist*, and a group of checkboxes that
 * appeared a second after the panel did would be worse than one that was never there.
 *
 * **An absent installation answers `sets: []` rather than failing**, so the `catch` here is for a
 * network fault alone. Either way `curatedSets` stays `[]` and `curatedRows` renders nothing: an
 * installation that has not been rsynced the lists is a working installation with three fewer
 * controls, and a failure notice for an absent optional feature is noise.
 */
let curatedSets: readonly CuratedSet[] = [];
const curatedCache = createCuratedCache();

async function loadCuratedSets(): Promise<void> {
  try {
    curatedSets = (await curatedLists()).sets;
  } catch {
    curatedSets = [];
  }
  for (const pane of panes) pane.controls.setCuratedSets(curatedSets);
}

/** `CuratedView` and `EncodedView` are the same three fields; a pane answers both the same way. */
const curatedViewOf = (pane: Pane): CuratedView | null => viewOf(pane);

/**
 * Draw this pane's curated overlays, fetching any coordinates it does not have (`V150`, P31).
 *
 * **The whole set is resolved and handed over in one call**, so precedence is decided once rather
 * than per glyph: `soonest` hides `candidates`, and a rule applied while drawing would show the
 * same point twice on one frame and once on the next.
 *
 * **Cached coordinates are drawn before the fetch, not after it.** Toggling a set the reader has
 * already looked at is then instant, and a first toggle draws nothing until its batch lands rather
 * than drawing half a set. What it must never do is draw a point at a coordinate it does not have
 * — `resolveGlyphs` skips those, which is `V115`'s rule.
 *
 * **The request is guarded against the view moving under it.** A 206-sequence batch behind a cold
 * `umap.joblib` is ~30 s, and the reader can change projection inside that window; placing the
 * answer would be the same `V115` failure `encodeAndPlace` guards against, so the view is re-read
 * after the await and a stale answer is cached but not drawn.
 */
async function showCurated(pane: Pane): Promise<void> {
  const view = curatedViewOf(pane);
  if (view === null || curatedSets.length === 0) {
    pane.renderer.setCuratedPoints([]);
    return;
  }
  const views = pane.controls.state().curated;
  const wanted = neededSequences(curatedSets, views);
  if (wanted.length === 0) {
    pane.renderer.setCuratedPoints([]);
    return;
  }

  drawCurated(pane, view, views);
  const missing = curatedCache.missing(view, wanted);
  if (missing.length === 0) return;

  // One request, not `missing.length` of them: `model.encode` takes a list, so this is one forward
  // pass. `runImport`'s sequential loop is the right shape for a reader watching each point land;
  // it is the wrong shape for a toggle.
  pane.curating = (pane.curating ?? 0) + 1;
  renderStatus(pane);
  try {
    const answer = await encodeBatch(view.atlasId, {
      sequences: missing,
      projection: view.projection,
    });
    curatedCache.put(view, missing, answer.xy);
    const now = curatedViewOf(pane);
    if (now === null || now.atlasId !== view.atlasId || now.projection !== view.projection) return;
    drawCurated(pane, now, pane.controls.state().curated);
  } catch (error) {
    // **Named, unlike the `/curated` 409.** That one means a feature is absent; this means a
    // feature the reader just switched on did not work, and the server's message says why — an
    // out-of-alphabet code names the row, which is the one thing the CSV cannot tell them.
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
  } finally {
    pane.curating = Math.max(0, (pane.curating ?? 1) - 1);
    renderStatus(pane);
  }
}

/** Resolve and hand over the glyphs for whatever coordinates are cached right now. */
function drawCurated(pane: Pane, view: CuratedView, views: ControlState['curated']): void {
  const glyphs = resolveGlyphs(curatedSets, views, curatedCache.forView(view));
  pane.renderer.setCuratedPoints(
    glyphs.map((g) => ({
      id: g.id,
      xy: g.xy,
      setClass: `set-${g.set}`,
      label: hasLandmarks(views[g.set]) ? g.short : null,
      // The full label including the substitution note, which the short form drops. A curated
      // point has no card, so the title is the only place the reader can read it.
      title: g.label === '' ? g.sequence : `${g.label}\n${g.sequence}`,
    })),
  );
}

/**
 * The channels a linked comparison propagates (P12.1).
 *
 * **Exactly `V18`'s three, and not the projection or the atlas.** A channel is a *rendering*
 * choice over the same points; a projection is a different set of coordinates and an atlas is a
 * different model. Propagating those would make the second pane follow the first into showing the
 * same thing, which is the one arrangement a comparison view must not silently produce.
 */
/**
 * One card for the whole page, shared by both comparison panes (`P18.2`).
 *
 * Shared rather than one per pane because it is a floating overlay positioned at the pointer:
 * two of them could both be open, and the reader would have no way to tell which pane the second
 * one belonged to.
 */
const residueCards = createResidueCards();

const LINKED: readonly (keyof ControlState)[] = ['colour', 'landmarks', 'dataset'];

let listing: AtlasListing | null = null;
let comparing = false;
/** `boot` runs again after a successful login, and the drag handle must not be wired twice. */
let resizerAttached = false;
/** Whether a channel change in one pane is applied to the other (P12.1). */
let linked = true;

// -- a pane ------------------------------------------------------------------------------------

/**
 * One map and everything that describes it.
 *
 * `sample` is the row count a raw-dimension view actually drew, and `null` for a tiled one. It is
 * held here rather than derived because it is the **server's** answer (P11.2): `n` asks for at
 * most `n` rows, and `V11`'s disclosure is a claim about what is on screen.
 */
/**
 * The two things the detail pane can show whose content depends on the trajectory (`V136`).
 *
 * An error and a `busy` message depend on nothing, so they are `null` here rather than a third case:
 * a walk change must not redraw a selection over the top of a refusal the reader has not read.
 */
type Shown =
  | {
      kind: 'selection';
      datum: PointDatum;
      /** The reconstruction card sitting beside it, which a redraw must not drop (P9.6). */
      reconstruction: DecodeResponse | null;
    }
  | {
      kind: 'decoded';
      result: DecodeResponse;
      xy: [number, number];
      /** `null` for a decode that has just happened, which is in no walk yet. */
      markId: string | null;
    }
  | {
      kind: 'encoded';
      /**
       * The record itself, not an id.
       *
       * **The one case here that can be shown for a record this pane is not drawing** — the
       * composer lists stale points and clicking one opens it, and a stale record is deliberately
       * absent from `pane.encoded` (`V139`). Holding the id would make the redraw look it up in a
       * map that does not have it.
       */
      mark: EncodedMark;
    };

interface Pane {
  readonly key: '' | '-b';
  readonly renderer: LatentRenderer;
  readonly controls: Controls;
  readonly detail: Detail;
  readonly health: Health;
  readonly status: HTMLElement;
  manifest: AtlasManifest | null;
  sample: number | null;
  /**
   * How many curated batches are in flight for this pane (P31).
   *
   * **A count rather than a flag**, because two toggles in quick succession are two requests and
   * the status line must clear only when the last one lands. Nothing serialises them: they are
   * idempotent — the same sequences resolve to the same coordinates — so the simpler rule is to
   * let both run and count.
   */
  curating?: number;
  /**
   * `row -> the datum it was pinned from, and the detach for its residue-card listeners`
   * (`V112`, `V116`, `V131`).
   *
   * Held per pane and keyed by row because a card is a fresh element per pinned point, and a
   * delegated listener that was only ever added would leak one set per click. The renderer holds
   * the nodes; this holds what has to be undone when one goes away.
   *
   * **The datum is held beside the detach as of `V131`**, for the same reason `marks` holds its
   * `DecodedMark`: a trajectory change re-labels and re-highlights every pinned card, and rebuilding
   * a card needs the datum it was built from. Without it, "which cards are on this map" would be
   * answerable and "rebuild them" would not.
   */
  readonly pins: Map<number, { datum: PointDatum; detach: () => void }>;
  /**
   * `mark id -> the record on screen, and the detach for its residue-card listeners` (`V123`).
   *
   * The same bookkeeping `pins` does and for the same reason: the marker node is rebuilt whenever
   * its card is opened or closed, and a delegated listener that was only ever added would leak one
   * set per toggle. It doubles as this pane's list of what is currently drawn — the renderer holds
   * the nodes and the store holds the records, and neither of those is "what is on this map now".
   *
   * **The `DecodedMark` is held beside the detach as of `V129`**, so a control can ask *which*
   * marks have their card open without going back to the store and re-filtering by view.
   */
  readonly marks: Map<string, { mark: DecodedMark; detach: () => void }>;
  /**
   * The typed sequences this pane is drawing, by `EncodedMark.id` (`V137`, P29).
   *
   * `marks`'s twin, and the same three jobs: the detach for the residue delegate, the record so a
   * control can ask *which* have their card open without re-filtering the store, and the key set
   * as this pane's answer to "what is on this map now".
   *
   * **Stale records are never in here.** They are in the store and listed in the composer; their
   * coordinates belong to an embedding that no longer exists, so `showEncoded` filters them out
   * rather than placing them somewhere the new fit never assigned (`V115`, `V139`).
   */
  readonly encoded: Map<string, { mark: EncodedMark; detach: () => void }>;
  /**
   * The ordered walk this pane is building, and what its diffs are measured against (`V131`, P27).
   *
   * **Per pane, not global.** An entry names an atlas **row**, and a row index only names a peptide
   * within one atlas; pane B defaults to a *different* atlas (`rebuildPanes`), so a shared list
   * would name rows that do not exist in one of the two panes.
   *
   * Replaced rather than mutated — the operations in `trajectory.ts` are pure — so a stale
   * reference cannot be half-updated.
   */
  trajectory: readonly TrajectoryPoint[];
  baseline: Baseline;
  /**
   * **What the detail pane is currently showing**, so that it can be shown again (`V136`).
   *
   * `Detail` is a renderer: it is handed everything derived and holds no model, which is the same
   * arrangement the trajectory panel has. That works for every input except one — the step label and
   * the marked residues are derived from **the walk**, which changes underneath a pane that has
   * already been drawn. Clearing the trajectory left `step 3 of 3 · 1 of 16 differ from step 1` and
   * a highlighted sequence sitting in the panel until the reader clicked the point again.
   *
   * So the pane's *input* is kept here, beside `pins` and `marks`, for the same reason those are:
   * "what is on this pane right now" is a question `main.ts` has to be able to answer in order to
   * redraw it. `null` for a pane showing nothing, an error, or a transient "decoding…".
   */
  shown: Shown | null;
  readonly trajectoryPanel: TrajectoryPanel;
  readonly encoderPanel: EncoderPanel;
  readonly interpolatePanel: InterpolatePanel;
  /**
   * The two endpoints this pane has picked, and the last walk it ran (`V143`, P30.11).
   *
   * **Per pane and not module-level**, for `importing`'s reason: an endpoint names a row of *one*
   * atlas or a coordinate of *one* projection, and pane B defaults to a different atlas.
   *
   * **`result` is the server's answer whole**, every step including the endpoints and the
   * duplicates, because the panel's summary is a claim about all of them (`V145`). `placed` is the
   * subset that went on the map, paired with the record id each became.
   *
   * `running.cancelled` is read after the `await` rather than aborting the request: a walk that has
   * reached the model will finish, and pretending otherwise would show a count that disagrees with
   * the map.
   */
  interpolation: {
    start: TrajectoryPoint | null;
    end: TrajectoryPoint | null;
    steps: number;
    includeEndpointMatches: boolean;
    result: readonly InterpolationStep[] | null;
    placed: readonly PlacedStep[];
    runId: string | null;
    /**
     * What the last run was computed from: its two references and the sampler that decoded it.
     *
     * Kept so that the endpoint-match switch can place or unplace those steps **from the answer
     * already in hand**, rather than issuing a second identical request or — worse — being a
     * control that changes nothing until the reader runs again.
     *
     * **The sampler is here rather than read off the control**, because the control can be changed
     * between the run and the re-place, and every record in this app names the sampler that
     * actually produced its sequence (§3.5).
     */
    refs: { start: LatentRef; end: LatentRef; sampler: string } | null;
    running: { cancelled: boolean; loadingProjection: boolean } | null;
  };
  /**
   * The bulk import running in this pane, or `null`.
   *
   * **Per pane and not module-level**, unlike the two stores: an import is a sequence of requests
   * against *one* atlas and *one* projection, which is a property of a pane rather than of a view.
   * Two panes may legitimately be importing into two different atlases at once.
   *
   * `cancelled` is read between requests rather than aborting one in flight — a request that has
   * already reached the model will finish and be placed, and pretending otherwise would show a
   * count that disagrees with the map.
   */
  importing: { rows: readonly ParsedRow[]; done: number; cancelled: boolean; first: boolean } | null;
  /**
   * The residue-card delegate's detaches for this pane's panels (`V131`).
   *
   * **`createResidueCards().attach` returns a detach and this pane used to throw it away.** The
   * listeners themselves died with the removed element, but `createResidueCards` keeps every root
   * it was given in a list and nothing removed the entry, so each `rebuildPanes` — every comparison
   * toggle, every panel-resize release — retained one detached panel subtree. Latent before Phase
   * 27; Phase 27 attaches a second root per pane, which would have doubled it per rebuild.
   */
  readonly detaches: (() => void)[];
}

const panes: Pane[] = [];

const paneOf = (key: '' | '-b'): Pane | undefined => panes.find((p) => p.key === key);

function element(tag: string, id: string, parent: HTMLElement): HTMLElement {
  const existing = document.querySelector(`#${CSS.escape(id)}`);
  if (existing !== null) return existing as HTMLElement;
  const node = document.createElement(tag);
  node.id = id;
  parent.append(node);
  return node;
}

/**
 * Every map's host element, created **before any of them is measured** (P12.1).
 *
 * `#maps` is a flex row, so how wide `#app` is depends on whether `#app-b` exists yet. Building
 * pane A and then pane B measured A while it was still the only child, and its canvas came out a
 * full row wide inside a half-row box — 752 px of canvas in a 376 px pane, so every click landed
 * at twice its distance from the left edge. Measured at P12.1, and the reason this function is
 * separate from `createPane` rather than the first two lines of it.
 */
function ensureHosts(): void {
  element('div', APP_ROOT_ID, maps);
  if (comparing) element('div', `${APP_ROOT_ID}-b`, maps);
}

/**
 * Build a pane, sizing its renderer from the element it is bound to.
 *
 * **The size is passed explicitly and that is not optional.** `Scatterplot`'s constructor falls
 * back to `window.innerWidth`/`innerHeight`, so a half-viewport pane would draw a full-width
 * canvas whose hit test disagrees with what the reader sees by exactly the overflow — see
 * `renderer.ts`. It also means a pane cannot be resized: `setComparing` destroys and remakes both.
 *
 * **And the size is rounded, which is a correctness fix rather than tidiness.** A flex row of two
 * panes over an odd number of pixels — 752 minus a 1 px divider — measures **375.5 and 376.5**.
 * deepscatter builds seven regl framebuffers from that number and regl rounds a texture's
 * dimensions independently of its framebuffer's, so a fractional width throws
 * `(regl) inconsistent width/height for supplied texture` from inside a render tick. `plotAPI`
 * never settles, so the failure arrives as a map that simply never appears rather than as a
 * rejected promise. Measured at P12.1.
 */
function createPane(key: '' | '-b'): Pane {
  ensureHosts();
  const host = document.querySelector(`#${CSS.escape(`${APP_ROOT_ID}${key}`)}`) as HTMLElement;
  const section = element('section', `pane${key || '-a'}`, panel);
  section.className = 'pane';
  const controlsRoot = element('div', `controls${key}`, section);
  // **Above the detail pane** (`V131`): the trajectory is a list the reader builds and keeps, and
  // the detail pane below it is a transient answer about one point.
  const trajectoryRoot = element('div', `trajectory${key}`, section);
  // **Below the trajectory and above the composer**, at the reader's asking: it is the tool used
  // *with* the walk — both are built from points clicked on the map — while the composer below is
  // where sequences are written from scratch. The order here must match `index.html`'s, which
  // declares pane A's roots.
  const interpolateRoot = element('div', `interpolate${key}`, section);
  // **A list the reader builds and keeps**, like the walk; the pane below stays a transient answer
  // about one point.
  const encoderRoot = element('div', `encoder${key}`, section);
  const detailRoot = element('div', `detail${key}`, section);
  const healthRoot = element('div', `health${key}`, section);
  // One delegate per panel root, and **both detaches are kept** — see `Pane.detaches`. The roots
  // are stable across every re-render, so this is attached once per pane rather than per render;
  // the pinned cards cannot do that, because the node itself is rebuilt.
  const detaches = [
    residueCards.attach(detailRoot),
    residueCards.attach(trajectoryRoot),
    // The third root, and the reason a chip carries `data-residue`: hovering a code being composed
    // shows the same structure card hovering one in a sequence does.
    residueCards.attach(encoderRoot),
    // The fourth root: an interpolated sequence's residues open the same structure card that a
    // corpus one does, and the panel lists them one line apart from the endpoints'.
    residueCards.attach(interpolateRoot),
  ];
  const status = element('p', `status${key}`, statusbar);

  const box = host.getBoundingClientRect();
  const width = Math.round(box.width);
  const height = Math.round(box.height);
  const pane: Pane = {
    key,
    renderer: createRenderer(`#${APP_ROOT_ID}${key}`, width, height),
    controls: createControls(controlsRoot, (changed, state) => void onControlChange(pane, changed, state), key),
    detail: createDetail(detailRoot, key),
    health: createHealth(healthRoot),
    status,
    manifest: null,
    sample: null,
    pins: new Map(),
    marks: new Map(),
    encoded: new Map(),
    importing: null,
    trajectory: [],
    // **`'start'` is the default, as asked**: "differs from the first point" is the question a
    // trajectory is usually built to answer, and `'previous'` is the per-step variant.
    baseline: 'start',
    shown: null,
    trajectoryPanel: createTrajectoryPanel(trajectoryRoot, key),
    encoderPanel: createEncoderPanel(encoderRoot, key),
    interpolatePanel: createInterpolatePanel(interpolateRoot, key),
    interpolation: {
      start: null,
      end: null,
      steps: DEFAULT_STEPS,
      includeEndpointMatches: false,
      result: null,
      placed: [],
      runId: null,
      refs: null,
      running: null,
    },
    detaches,
  };
  attachClicks(pane);
  // **Here rather than only in `loadCuratedSets`**, because panes outlive and predate that fetch
  // in both directions: pane A is created after it at boot, and `rebuildPanes` recreates both on
  // every comparison toggle and panel resize, long after it has resolved. Seeding at construction
  // is the one place that covers all three.
  pane.controls.setCuratedSets(curatedSets);
  panes.push(pane);
  return pane;
}

function destroyPane(pane: Pane): void {
  clearPin(pane);
  clearMarks(pane);
  clearEncodedMarks(pane);
  // An import outlives its own `await` and would otherwise go on placing rows into a pane that no
  // longer exists — `rebuildPanes` destroys both panes on a comparison toggle or a panel resize.
  if (pane.importing !== null) pane.importing.cancelled = true;
  // A run outlives its own `await` for the same reason an import does.
  if (pane.interpolation.running !== null) pane.interpolation.running.cancelled = true;
  for (const detach of pane.detaches) detach();
  pane.renderer.destroy();
  panes.splice(panes.indexOf(pane), 1);
  for (const id of [`${APP_ROOT_ID}${pane.key}`, `pane${pane.key || '-a'}`, `status${pane.key}`]) {
    document.querySelector(`#${CSS.escape(id)}`)?.remove();
  }
}

// -- what is on screen -------------------------------------------------------------------------

/**
 * A `ProjectionSpec` for whatever the pane is showing, synthesising one for a raw pair (`V65`).
 *
 * **A raw view has no manifest entry by construction** — `C(D, 2)` pairs makes one per pair
 * impossible (`V12`) — so the coverage figures that `V11`'s disclosure reads have to come from
 * somewhere else, and the only party that knows them is the server that chose the rows. Hence
 * `pane.sample`. `n_fitted` equals `n_covered` because nothing was fitted: a raw view is a column
 * slice, so the "fitted on a subsample" line below must stay off.
 */
function specOf(pane: Pane): ProjectionSpec | undefined {
  const state = pane.controls.state();
  if (pane.manifest === null) return undefined;
  const pair = parseRawProjection(state.projection);
  if (pair === null) return pane.controls.projectionSpec(pane.manifest);
  const covered = pane.sample ?? 0;
  if (covered <= 0) return undefined;
  return {
    name: state.projection,
    kind: 'raw',
    n_covered: covered,
    n_fitted: covered,
    params: { i: pair[0], j: pair[1] },
    tiled: false,
  };
}

/**
 * **The `V11` disclosure, and it is not optional.** A projection covering fewer rows than the
 * corpus holds must say so beside the map: a view showing 1 M of 100 M points that does not
 * announce it is the failure `V11` exists to prevent, and comparing `n_covered` against
 * `n_corpus_rows` is what catches it. The two questions differ — `is_subsampled` is about how
 * trustworthy the embedding is, this is about what is missing from the screen.
 *
 * **The denominator is `n_corpus_rows`, not `n_rows`** (`V83`, P15.7). Until Phase 15 they were
 * the same number and either would do. A precomputed subsample makes the atlas 100,000 rows of a
 * 10,000,000-row corpus, and against `n_rows` a PCA covering every atlas row would announce
 * itself complete — true of the atlas, and the exact lie about the corpus that `V11` forbids. The
 * third line states the sample itself, because "100,000 of 10,000,000" alone does not say whether
 * the other 9.9 M are missing from the *view* or from the *atlas*.
 *
 * **A raw-dimension view is a sample by construction and says the word** (P11.2). It is the one
 * view where partial coverage is not a property of the fit but of the transfer: the pair is a
 * column slice over every atlas row, and what was sent is `min(n, n_rows)` of them.
 */
function renderStatus(pane: Pane): void {
  const state = pane.controls.state();
  if (pane.manifest === null || listing === null) return;
  const manifest = pane.manifest;
  const spec = specOf(pane);
  const pair = parseRawProjection(state.projection);
  const parts = [
    `${state.atlasId} / ${pair === null ? state.projection : `raw dimensions ${pair[0]} and ${pair[1]}`}`,
  ];
  if (spec !== undefined) {
    const corpus = manifest.n_corpus_rows.toLocaleString();
    if (pair !== null) {
      parts.push(`sample of ${spec.n_covered.toLocaleString()} of ${corpus} rows`);
    } else {
      parts.push(`${spec.n_covered.toLocaleString()} of ${corpus} rows`);
    }
    if (spec.n_covered < manifest.n_corpus_rows) {
      parts.push('PARTIAL VIEW — this projection does not cover the whole corpus');
    }
    if (manifest.n_rows < manifest.n_corpus_rows) {
      parts.push(`the atlas is a ${manifest.n_rows.toLocaleString()}-row sample of the corpus`);
    }
    if (spec.n_fitted < spec.n_covered) {
      parts.push(`fitted on a ${spec.n_fitted.toLocaleString()}-row subsample`);
    }
  }
  parts.push(`resident ${listing.resident.length}/${listing.capacity}`);
  // **The one thing on this line that is not a fact about the atlas** (P31). A first curated
  // toggle on a cold projection waits on `umap.joblib`, measured at 30.7 s for 92 MB, and a map
  // that shows nothing for half a minute reads as broken. The composer's own progress line says
  // this for a paste; a toggle has no panel of its own, so it says it here.
  if ((pane.curating ?? 0) > 0) parts.push('placing curated peptides…');
  pane.status.textContent = parts.join(' · ');
}

/**
 * Report an eviction, by name (P12.2, `V17`).
 *
 * **The server names what it dropped; this does not diff `resident` against its own last answer.**
 * That diff is against a snapshot another tab may already have invalidated, and a notice naming
 * the wrong atlas is worse than no notice at all. Rendered rather than logged because §4's point
 * is that a silent eviction during a comparison is indistinguishable from a bug, and the log is
 * read by whoever runs the server rather than by whoever is looking at the screen.
 */
function showEviction(evicted: readonly string[]): void {
  if (evicted.length === 0) return;
  noticeRoot.replaceChildren(
    `evicted ${evicted.join(', ')} — at most ${listing?.capacity ?? 2} atlases stay resident (V17). `,
    'Reloading one costs a full projection load. (click to dismiss)',
  );
  noticeRoot.hidden = false;
  // Dismissible, because it reports an **event** rather than a state. Left up, it would still be
  // true an hour later and would read as a description of the view, which the status line is.
  noticeRoot.onclick = () => {
    noticeRoot.hidden = true;
  };
}

// -- loading -----------------------------------------------------------------------------------

/**
 * Make an atlas resident, then draw it in this pane.
 *
 * The order is: load the manifest, read the latent statistics, then the view, then the widgets.
 * **The statistics come before the widgets because the raw-dimension pickers are ordered by
 * per-dimension activity** (P11.3), and a picker rendered before they arrive would open on
 * dimensions 0 and 1 and then silently reorder under the reader's cursor.
 */
async function showAtlas(pane: Pane): Promise<void> {
  const state = pane.controls.state();
  const loaded = await loadAtlas(state.atlasId);
  pane.manifest = loaded.atlas;
  if (listing !== null) {
    listing.resident = loaded.resident;
    listing.capacity = loaded.capacity;
  }
  showEviction(loaded.evicted ?? []);
  const manifest = loaded.atlas;

  // A raw pair survives an atlas change if the new atlas is wide enough for it; anything else
  // falls back to the manifest's first projection.
  const pair = parseRawProjection(state.projection);
  const keepRaw =
    pair !== null &&
    pair[0] !== pair[1] &&
    pair.every((d) => d >= 0 && d < manifest.latent_dim);
  if (!keepRaw) {
    const spec =
      manifest.projections.find((p) => p.name === state.projection) ?? manifest.projections[0];
    if (spec === undefined) throw new Error(`${state.atlasId} declares no projections`);
    pane.controls.set({ projection: spec.name });
  }

  const palettes = manifest.columns.filter(
    (c) => c.role === 'categorical' || c.role === 'continuous',
  );
  // **`''` is exempt from the fallback** (`V154`, P31). This exists so that switching to an atlas
  // that does not declare the column the reader was colouring by lands on something real rather
  // than on a channel the tiles have no field for. Flat is real: it names no column, so no atlas
  // can fail to provide it, and a reader who asked for a flat map must not have one imposed on
  // them by an atlas switch.
  if (
    state.colour !== '' &&
    palettes.find((c) => c.name === state.colour) === undefined &&
    palettes[0] !== undefined
  ) {
    pane.controls.set({ colour: palettes[0].name });
  }

  clearPin(pane);
  await showHealth(pane);
  await showView(pane);
  applyChannels(pane);
  showMarks(pane);
  showEncoded(pane);
  // **Not awaited.** A curated batch behind a cold reducer is ~30 s and nothing below depends on
  // it; awaiting here would hold the whole atlas switch — panels, cards, status — behind an
  // overlay the reader may not even have switched on. `showCurated` re-reads the view after its
  // await, so an atlas change inside that window discards the answer rather than misplacing it.
  void showCurated(pane);
  // **After the projection has settled, not with `clearPin` above.** `showAtlas` can replace a
  // projection the new atlas does not declare, and the panel's placeable/unplaceable state is read
  // off the projection on screen (`V131`).
  renderTrajectory(pane);
  // Same reason, and the panel's own forecast is read off the store for the view that has settled.
  renderInterpolate(pane);
  renderPanel();
  clearDetail(pane);
}

/**
 * Point the pane's renderer at whatever its projection names — tiles, or an Arrow buffer.
 *
 * **The two paths differ only in where the points come from**, which is the property that lets one
 * click handler, one set of channels and one detail pane serve both. `/rawdims` returns the same
 * column names a tile carries (`V51`: `ix` the rendering rank, `row` the identity), so nothing
 * downstream of here knows which it is looking at.
 */
async function showView(pane: Pane): Promise<void> {
  const state = pane.controls.state();
  const manifest = pane.manifest;
  if (manifest === null) return;
  const pair = parseRawProjection(state.projection);
  if (pair !== null) {
    const answer = await rawDims(state.atlasId, pair[0], pair[1]);
    pane.sample = answer.sample;
    await pane.renderer.setArrowBuffer(state.projection, answer.buffer);
    return;
  }
  pane.sample = null;
  // Every declared column, so the detail pane can show the whole datum without a round trip.
  // `V13`'s guarantee is only true of the *loaded* batch if the columns are asked for — see
  // `IDENTITY_COLUMNS` in `renderer.ts`.
  await pane.renderer.setDeeptable(
    tilesUrl(state.atlasId, state.projection),
    manifest.columns.map((c) => c.name),
  );
}

/** The three columns of `V18` that are a *rendering* choice, applied together. */
function applyChannels(pane: Pane): void {
  const state = pane.controls.state();
  if (state.colour === '') {
    // **`''` is now a choice and is acted on** (`V154`, P31). Before this it meant "nothing has
    // been chosen yet" and was skipped — which left whatever colour was last applied, because
    // `setDeeptable` re-applies the channel across a projection switch. Skipping was therefore
    // never "no colour"; it was "the previous colour", and the select had no way to ask for flat.
    pane.renderer.clearColorChannel();
  } else {
    const role = pane.manifest?.columns.find((c) => c.name === state.colour)?.role ?? 'categorical';
    pane.renderer.setColorChannel(state.colour, role);
  }
  pane.renderer.setHighlight(state.landmarks);
  applyDataset(pane, state.dataset);
}

const SPLIT_OF: Record<Dataset, readonly string[] | null> = {
  full: null,
  train: ['train'],
  val: ['val'],
  test: ['test'],
};

function applyDataset(pane: Pane, dataset: Dataset): void {
  const wanted = SPLIT_OF[dataset];
  pane.renderer.setFilter(wanted === null ? null : 'split', wanted ?? []);
}

/**
 * Load the health panel's data (P10.4, `V19`), and the activity ordering the pickers use (P11.3).
 *
 * **A 404 is reported rather than swallowed.** An atlas built before the panel existed has no
 * `latent_stats.json`, and an empty panel would read as a clean bill of health — which is the one
 * thing this panel must never say by accident. The pickers fall back to index order and say so.
 */
async function showHealth(pane: Pane): Promise<void> {
  const state = pane.controls.state();
  try {
    const payload = (await latentStats(state.atlasId)) as unknown as LatentStatsPayload;
    pane.health.show(payload);
    pane.controls.setActivity(payload.per_dim_mu_variance ?? null);
  } catch (error) {
    pane.controls.setActivity(null);
    pane.health.unavailable(
      error instanceof ApiError
        ? error.detail
        : `latent statistics could not be read: ${String(error)}`,
    );
  }
}

async function onControlChange(
  pane: Pane,
  changed: keyof ControlState,
  state: ControlState,
): Promise<void> {
  try {
    if (changed === 'atlasId') {
      // **The walk is emptied here and not in `showAtlas`** (`V131`). An entry names an atlas row,
      // and a row index names a different peptide in a different atlas — but `showAtlas` also runs
      // at boot and on a pane rebuild, where the atlas has not changed and the walk must survive.
      pane.trajectory = [];
      // An endpoint names a row of *this* atlas, or a coordinate of one of its projections, so an
      // atlas change empties both slots as it empties the walk.
      pane.interpolation = {
        ...pane.interpolation,
        start: null,
        end: null,
        result: null,
        placed: [],
        runId: null,
      };
      await showAtlas(pane);
      return;
    }
    if (changed === 'projection') {
      clearPin(pane);
      await showView(pane);
      applyChannels(pane);
      // **After the view, never before.** A mark is placed through the zoom's scales, and until
      // `showView` has rebound them those are still the previous projection's — the `V115` failure
      // that put eight landmarks 11,000 px off a 1,200 px canvas. `renderer.mark` re-places on a
      // bounded window for the same reason.
      showMarks(pane);
      showEncoded(pane);
      // Same reason, and the same guard: a coordinate belongs to one projection, so the cache is
      // keyed by it and the new projection starts empty unless the reader has been here before.
      void showCurated(pane);
      // **The walk survives, its coordinates do not** (`V131`). Every entry is still a real row
      // with a real sequence, so the list and the diffs stay; `datum.x`/`.y` now belong to the
      // previous embedding, so the panel marks those entries unplaceable rather than offering to
      // put a card somewhere the new projection never assigned to that row (`V115`).
      renderTrajectory(pane);
      // **The endpoints survive and the result does not** (`V115`, P30.11). An endpoint is still a
      // real point and the panel says in words that its coordinates belong elsewhere; the walk's
      // steps are coordinates of the projection that has just been left, so there is nothing
      // honest to keep showing.
      pane.interpolation = { ...pane.interpolation, result: null, placed: [], runId: null };
      renderInterpolate(pane);
      renderPanel();
      return;
    }
    if (changed === 'mode' || changed === 'fill') {
      // **The panel is re-rendered, and that is what makes the fill selectable at all** (`V110`).
      // `fill` and the help line under it are both derived from `mode` — the fill is greyed out
      // until the decoder tool is picked up — and nothing here used to re-render on a mode change.
      // So switching to the decoder tool left the fill select disabled, showing whatever it was
      // rendered with, until some *other* control forced a render. Nothing is reloaded: this
      // rebuilds one panel's widgets and touches neither the tiles nor the channels.
      if (pane.manifest !== null && listing !== null) pane.controls.render(listing, pane.manifest);
      // The panel's empty state depends on the mode: with nothing in the walk it is absent, and in
      // trajectory mode it is one line saying where the output will appear.
      renderTrajectory(pane);
      // The same for the interpolation panel, and its help line also depends on the sampler the
      // fill row sits beside.
      renderInterpolate(pane);
      renderStatus(pane);
      return;
    }
    if (changed === 'curated') {
      // **Its own branch, not folded into the channel branch below.** A channel is a `plotAPI`
      // call on points already loaded; this can be a model round trip, so it is asynchronous and
      // it reports progress. Linked panes follow it for the same reason they follow a colour —
      // it is a rendering choice over the same peptides, not a different set of them.
      const followers = linked ? panes.filter((other) => other !== pane) : [];
      for (const other of followers) {
        if (other.manifest === null) continue;
        other.controls.set({ curated: state.curated });
        if (other.manifest !== null && listing !== null) {
          other.controls.render(listing, other.manifest);
        }
        void showCurated(other);
      }
      void showCurated(pane);
      // The row's own checkboxes were just clicked and already show the truth, but the *labels*
      // box has to be enabled or disabled by the show box beside it, and that is `render`'s job.
      if (pane.manifest !== null && listing !== null) pane.controls.render(listing, pane.manifest);
      if (followers.length > 0) renderPanel();
      renderStatus(pane);
      return;
    }
    if (changed === 'colour' || changed === 'landmarks' || changed === 'dataset') {
      // **Linked channels propagate; everything else stays local** (P12.1). The other pane is set
      // rather than told to emit, so one user gesture is one round of work rather than a loop of
      // two panes notifying each other.
      const followers = linked ? panes.filter((other) => other !== pane) : [];
      for (const other of followers) {
        if (other.manifest === null) continue;
        const patch: Partial<ControlState> = {};
        for (const field of LINKED) (patch as Record<string, unknown>)[field] = state[field];
        other.controls.set(patch);
        applyChannels(other);
      }
      applyChannels(pane);
      if (followers.length > 0) renderPanel();
    }
    renderStatus(pane);
  } catch (error) {
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
  }
}

// -- what a click means ------------------------------------------------------------------------

/**
 * What a click does, per mode (`V14`, `V15`, `V131`).
 *
 * **`trajectory` is `inspect` plus an append, and the order matters**: the point joins the walk
 * first, so the detail pane and the card that `selectPoint` produces already carry the step label
 * and the highlight rather than being relabelled a moment later.
 *
 * **Neither of the two inspect-like modes issues a request**, which is §9's row *"inspect mode
 * issues no request"* and now covers both: the sequence is in the tile (`V13`) and the diff is
 * arithmetic over two strings.
 */
function attachClicks(pane: Pane): void {
  pane.renderer.onPointClick((datum) => {
    const mode = pane.controls.state().mode;
    if (mode === 'decode') {
      void decodeAt(pane, [datum.x, datum.y]);
      return;
    }
    // **Selection, not an action** (`V143`). The click names one end of a walk; the run is the
    // panel's button, because a walk needs both ends before it can be computed.
    if (mode === 'interpolate') selectEndpoint(pane, storedPoint(datum, pane.controls.state().projection));
    if (mode === 'trajectory') appendToTrajectory(pane, datum);
    selectPoint(pane, datum);
  });

  pane.renderer.onEmptyClick((xy) => {
    const mode = pane.controls.state().mode;
    if (mode === 'decode') {
      void decodeAt(pane, xy);
      return;
    }
    // **Inspect over empty space still issues no request.** `nearestRendered` searches the loaded
    // tiles, which *are* the visible region; `null` means nothing is near and the app selects
    // nothing rather than reaching for a point that is off screen (§8).
    const nearest = pane.renderer.nearestRendered(xy);
    if (nearest === null) {
      clearDetail(pane);
      return;
    }
    // Parity with a click on a point, deliberately: the mode is "inspect plus append", and a
    // near-miss that selected without appending would make the tool feel unreliable. The same
    // parity applies to picking an endpoint.
    if (mode === 'interpolate') {
      selectEndpoint(pane, storedPoint(nearest, pane.controls.state().projection));
    }
    if (mode === 'trajectory') appendToTrajectory(pane, nearest);
    selectPoint(pane, nearest);
  });
}

function selectPoint(pane: Pane, datum: PointDatum): void {
  showSelected(pane, datum);
  // **Clicking a point that is already pinned changes nothing on the map** (`V116`). It was a
  // toggle at P19.6 and the user asked for accumulation instead; a repeat click still updates the
  // detail pane, because that pane shows one point by construction.
  if (pane.pins.has(datum.row)) return;
  pinSelection(pane, datum);
  // **And the panel is redrawn after the pin, not before it** (`V148`). In trajectory mode the
  // click appends first — deliberately, so the card this call builds already carries its step
  // label — and `appendToTrajectory` renders the panel while `pane.pins` does not yet hold this
  // row. `statusOf` read that as `placeable`, so the panel offered a `place` control for a card
  // that was already on the map, and nothing re-rendered it until some other gesture did.
  //
  // Only when this point is *in* the walk: outside it the panel says nothing about this row, and
  // `renderTrajectory` rebuilds every card on the map.
  if (stepOfKey(pane, rowKey(datum.row)) !== undefined) renderTrajectory(pane);
}

/**
 * Pin a clicked point's sequence to the map, with its residues live (`V112`, `V116`, `P19.6`).
 *
 * The card is attached to the shared residue-card delegate exactly as a detail pane is, which is
 * what makes hovering a code on the map open the same structure card it opens in the panel — one
 * implementation, one cache, one structure card on screen at a time however many points are
 * pinned.
 */
function pinSelection(pane: Pane, datum: PointDatum, step = stepOfKey(pane, rowKey(datum.row))): void {
  const card = pinnedPointCard(
    datum,
    () => unpinOne(pane, datum.row),
    step,
    // Across every pane holding this row, for `setMarkCard`'s reason: one point, one card, and two
    // panes on the same atlas are looking at the same peptide.
    cardNotes(rowKey(datum.row), () => {
      for (const other of panes) {
        const held = other.pins.get(datum.row);
        if (held !== undefined) pinSelection(other, held.datum);
      }
    }),
  );
  pane.pins.get(datum.row)?.detach();
  pane.pins.set(datum.row, { datum, detach: residueCards.attach(card) });
  // `renderer.pin` replaces the node for a row it already holds **and keeps the drag offset**
  // (`V131`), which is what makes `refreshCards` safe to run on every trajectory change.
  pane.renderer.pin(datum, card);
  renderMapActions();
}

/** Remove one card — what its own close control calls. */
function unpinOne(pane: Pane, row: number): void {
  pane.pins.get(row)?.detach();
  pane.pins.delete(row);
  pane.renderer.unpin(row);
  // **The trajectory is not touched** (`V131`). Closing a card is a dismissal; the entry stays in
  // the walk and the panel offers to place it again.
  renderTrajectory(pane);
}

/**
 * Take every card off this pane's map and detach their listeners.
 *
 * **Called on a projection change as well as by "clear all"**, and that is a correctness
 * requirement rather than tidiness: a card is placed from `datum.x`/`datum.y`, which are *this
 * projection's* coordinates for that row. Left up across a switch they would sit at positions the
 * new projection never assigned to anything.
 */
function clearPin(pane: Pane): void {
  for (const { detach } of pane.pins.values()) detach();
  pane.pins.clear();
  pane.renderer.clearPins();
  // **Every card, and not one entry of the walk** (`V129`'s rule, extended by `V131`): everything
  // this clears can be brought back — by clicking the point again, or from the panel's `place`.
  renderTrajectory(pane);
}

// -- the trajectory: an ordered walk, and the residues that differ along it (`V131`, P27) -------

/**
 * This point's place in the walk, or `undefined` if it is not in it.
 *
 * **Keyed by `keyOf`'s string rather than by a row** (`V132`): the walk may hold decoded points,
 * whose identity is a `decoded.ts` record id and not an atlas row. Callers pass `rowKey(row)` or
 * `markKey(id)`, which are the only two spellings of a key in the app.
 *
 * Recomputed from the list rather than stored on the entry, which is `steps`'s own rule: a stored
 * step number is a second source of truth for the array's order, and the first reorder that forgot
 * to rewrite it would leave two entries claiming to be step 3.
 */
function stepOfKey(pane: Pane, key: string): Step | undefined {
  const at = indexOfKey(pane.trajectory, key);
  if (at < 0) return undefined;
  return steps(pane.trajectory, pane.baseline)[at];
}

/**
 * Rebuild **every** card on this pane's map — pinned and decoded, member or not (`V131`, `V132`).
 *
 * Three reasons it is all of them rather than only the walk's:
 *
 * 1. changing the start, or switching the baseline, changes what every member's card says;
 * 2. **removing an entry has to take the label and the highlight off a card that stays** — the
 *    reader's rule that removal is not closing (`V131`), and the removed point is no longer in the
 *    list to be iterated;
 * 3. a card is rebuilt rather than patched, which is `drawMark`'s own rule for the same reason: the
 *    node is the whole of the update, so there is no partial state to keep in step. The residue
 *    delegate is re-attached with it, because the spans in the new node are new elements.
 *
 * **Both overlays, since Phase 28**, because a decoded point can now be a step of the walk and its
 * card carries the same label and the same marked residues a pinned one does. Renamed from
 * `refreshPins` for exactly that reason.
 *
 * Safe to run on every change **only because `renderer.pin` carries the drag offset forward**
 * (`V131`) and `renderer.mark` always did. Without that, adding a sixth point would yank the five
 * cards the reader had arranged back to their resting positions, undoing Phases 24-26 in one click.
 */
function refreshCards(pane: Pane): void {
  const byKey = new Map<string, Step>();
  steps(pane.trajectory, pane.baseline).forEach((step, at) => {
    const point = pane.trajectory[at];
    if (point !== undefined) byKey.set(keyOf(point), step);
  });
  for (const { datum } of [...pane.pins.values()]) {
    pinSelection(pane, datum, byKey.get(rowKey(datum.row)));
  }
  // **Only the decoded points whose card is open** (`V132`). A closed diamond draws no label and
  // no residues, so there is nothing on it a trajectory change could alter; rebuilding all of them
  // would rebuild up to `CAP_PER_VIEW` = 200 nodes on every click. The snapshot is required because
  // `drawMark` writes to `pane.marks`, which is the loop's own source.
  for (const { mark } of [...pane.marks.values()]) {
    if (mark.card) drawMark(pane, mark, byKey.get(markKey(mark.id)));
  }
  // The same rule for the third glyph, and the same snapshot for the same reason.
  for (const { mark } of [...pane.encoded.values()]) {
    if (mark.card) drawEncoded(pane, mark, byKey.get(encodedKey(mark.id)));
  }
}

/** Everything the panel can do, wired to this pane. Rebuilt per render; it holds no state. */
function trajectoryHandlers(pane: Pane): TrajectoryHandlers {
  return {
    onMove: (at, by) => {
      pane.trajectory = moveBy(pane.trajectory, at, by);
      renderTrajectory(pane);
    },
    onRemove: (at) => {
      pane.trajectory = removeAt(pane.trajectory, at);
      renderTrajectory(pane);
    },
    // **Shown in the pane, and not pinned.** Placing is the separate control beside it, because an
    // entry from another projection must not be placed at all — see `placePoint`.
    // **Each kind opens in its own pane card** (P9.5): a corpus row as a selection with its
    // properties and a reconstruction control, a decoded point as the generated card with the
    // sampler and fill that wrote it. One `showSelection` for both would style a generated
    // sequence as stored, which is the distinction this app does not blur.
    onSelect: (at) => {
      const point = pane.trajectory[at];
      if (point === undefined) return;
      if (point.kind === 'stored') {
        showSelected(pane, point.datum);
        return;
      }
      if (point.kind === 'encoded') {
        showEncodedPoint(pane, point.mark);
        return;
      }
      showDecodedPoint(pane, point.mark.result, point.mark.xy, point.mark.id);
    },
    onPlace: (at) => {
      placePoint(pane, at);
      renderTrajectory(pane);
    },
    onPlaceAll: () => {
      for (let at = 0; at < pane.trajectory.length; at++) placePoint(pane, at);
      renderTrajectory(pane);
    },
    onClear: () => {
      // **The cards stay** (`V131`). They lose their step labels and their highlights on the next
      // line, which is exactly what leaving the walk means.
      pane.trajectory = [];
      renderTrajectory(pane);
    },
    onBaseline: (baseline) => {
      pane.baseline = baseline;
      renderTrajectory(pane);
    },
  };
}

/**
 * Put one entry's card back on the map, **refusing anything `statusOf` would not call placeable**.
 *
 * The panel disables those controls, so this is the backstop rather than the guard the reader
 * meets. It exists because an entry's coordinates are the coordinates of *its own* projection, and
 * placing them through another projection's scale is the `V115` failure that put eight landmarks
 * 11,000 px off a 1,200 px canvas.
 *
 * The two kinds are placed by two mechanisms and that is not incidental: a corpus row is pinned by
 * row, while a decoded point's diamond is already on the map and what "place" means for it is
 * **opening its card**, which is a change to the stored record (`decoded.ts`) rather than to this
 * pane.
 */
function placePoint(pane: Pane, at: number): void {
  const point = pane.trajectory[at];
  if (point === undefined) return;
  if (statusOf(pane, point) !== 'placeable') return;
  if (point.kind === 'stored') {
    pinSelection(pane, point.datum);
    return;
  }
  if (point.kind === 'generated') {
    const held = pane.marks.get(point.mark.id);
    if (held !== undefined) setMarkCard(held.mark, true);
    return;
  }
  const held = pane.encoded.get(point.mark.id);
  if (held !== undefined) setEncodedCard(held.mark, true);
}

/**
 * Whether this entry's card is up, can be put up, or cannot — and why not (`V132`).
 *
 * The one place the four values are decided, because it is the one function with all three facts:
 * the walk, what this pane has drawn, and which projection is on screen.
 *
 * **`gone` is asked of `pane.marks` rather than of the store**, and that is the accurate question.
 * `pane.marks` is what this pane has actually drawn for the view it is showing, so any record that
 * is no longer on the map lands in the same state — there is no diamond to open a card on. The
 * projection is checked first, so an entry from another projection says that rather than claiming
 * its record is gone.
 *
 * Since `V133` a **deleted** point is not one of these: `dropFromWalks` takes its entry out of every
 * walk, so what reaches `gone` is the record that disappeared without a gesture — `CAP_PER_VIEW`'s
 * eviction, which must not renumber a walk.
 */
function statusOf(pane: Pane, point: TrajectoryPoint): EntryStatus {
  if (point.projection !== pane.controls.state().projection) return 'off-projection';
  if (point.kind === 'stored') return pane.pins.has(point.datum.row) ? 'placed' : 'placeable';
  // **Asked of what this pane has drawn, not of the store**, which is the accurate question: a
  // record the store still holds but this pane is not drawing is not on this map. For a typed
  // point that covers one state a decoded point cannot reach — a rebuilt atlas leaves the record
  // in storage and off the map (`V139`), and the entry says so rather than offering a `place`
  // control that would put a card at a coordinate this build never assigned.
  const held =
    point.kind === 'generated' ? pane.marks.get(point.mark.id) : pane.encoded.get(point.mark.id);
  if (held === undefined) return 'gone';
  return held.mark.card ? 'placed' : 'placeable';
}

/**
 * Show one corpus point in the detail pane, **and remember that it is what is showing** (`V136`).
 *
 * Every path that puts a selection in the pane goes through here — a click, the panel's ordinal, and
 * `reconstruct` — so there is one place that knows how to draw it and one place that records it. The
 * step is looked up rather than passed in, which is what makes a redraw after a walk change correct
 * by construction instead of by every caller remembering.
 *
 * **`reconstruction` is carried rather than dropped** (P9.6): the reconstruction sits *beside* the
 * stored sequence and never replaces it, so a redraw that lost it would undo the one thing that pane
 * arrangement exists for. `reconstruct` calls this twice — once before its request and once with the
 * response — which is what it already did.
 */
function showSelected(
  pane: Pane,
  datum: PointDatum,
  reconstruction: DecodeResponse | null = null,
): void {
  pane.shown = { kind: 'selection', datum, reconstruction };
  pane.detail.showSelection(
    datum,
    () => {
      void reconstruct(pane, datum);
    },
    stepOfKey(pane, rowKey(datum.row)),
  );
  if (reconstruction !== null) pane.detail.showReconstruction(reconstruction);
}

/** The same for a decoded point. `markId` is `null` for a decode that is in no walk yet (`V136`). */
function showDecodedPoint(
  pane: Pane,
  result: DecodeResponse,
  xy: [number, number],
  markId: string | null,
): void {
  pane.shown = { kind: 'decoded', result, xy, markId };
  pane.detail.showDecoded(
    result,
    xy,
    markId === null ? undefined : stepOfKey(pane, markKey(markId)),
  );
}

/**
 * Empty the detail pane, or fill it with something a walk change must not overwrite (`V136`).
 *
 * Three states share one rule: nothing, a refusal, and a transient "decoding…" are all things
 * `refreshDetail` must leave alone, and setting `shown` to `null` is how it is told.
 */
/**
 * Show a typed sequence in the pane, and remember that it is what the pane is showing (`V136`).
 *
 * The `Shown` record is what lets a walk change re-derive the step label and the highlight — the
 * bug `V136` fixed for the other two kinds, which this one would otherwise have shipped with.
 */
function showEncodedPoint(pane: Pane, mark: EncodedMark): void {
  pane.shown = { kind: 'encoded', mark };
  pane.detail.showEncoded(mark, stepOfKey(pane, encodedKey(mark.id)));
}

function clearDetail(pane: Pane): void {
  pane.shown = null;
  pane.detail.clear();
}

function detailError(pane: Pane, message: string): void {
  pane.shown = null;
  pane.detail.showError(message);
}

function detailBusy(pane: Pane, what: string): void {
  pane.shown = null;
  pane.detail.busy(what);
}

/**
 * Draw the detail pane again, with whatever the walk now says about the point in it (`V136`).
 *
 * **The counterpart of `refreshCards`, and it was missing.** Phase 27 gave the map cards a
 * carry-forward for a walk change and left the panel with none, so `clear the trajectory` took the
 * step labels off three cards on the map and left the same label in the panel beside them.
 *
 * A no-op when the pane holds nothing trajectory-dependent, which is what `shown === null` means.
 */
function refreshDetail(pane: Pane): void {
  const shown = pane.shown;
  if (shown === null) return;
  if (shown.kind === 'selection') {
    showSelected(pane, shown.datum, shown.reconstruction);
    return;
  }
  if (shown.kind === 'encoded') {
    showEncodedPoint(pane, shown.mark);
    return;
  }
  showDecodedPoint(pane, shown.result, shown.xy, shown.markId);
}

/**
 * Draw the panel, then bring every card on the map into step with it.
 *
 * **One function, called after every change to the walk and after every change to what is on
 * screen.** The panel and the cards read the same list, so a path that re-rendered one without the
 * other is a path where a card claims to be step 3 of a walk the panel says has two points.
 */
function renderTrajectory(pane: Pane): void {
  const state = pane.controls.state();
  pane.trajectoryPanel.show(
    {
      points: pane.trajectory,
      steps: steps(pane.trajectory, pane.baseline),
      baseline: pane.baseline,
      status: new Map(pane.trajectory.map((point) => [keyOf(point), statusOf(pane, point)])),
      active: state.mode === 'trajectory',
    },
    trajectoryHandlers(pane),
  );
  refreshCards(pane);
  // **And the panel beside them** (`V136`). The map and the detail pane read one list, so a path
  // that refreshed one without the other is a path where the panel says `step 3 of 3` about a walk
  // the map no longer draws.
  refreshDetail(pane);
  renderMapActions();
}

/**
 * Add a clicked corpus point to the end of the walk.
 *
 * A point already in the walk is ignored by `append` — one sequence cannot hold two step numbers,
 * and its card is keyed by the same identity so only one of them could be drawn. The click still
 * updates the detail pane, which is what a repeat click in inspect mode already does.
 */
function appendToTrajectory(pane: Pane, datum: PointDatum): void {
  pane.trajectory = appendPoint(pane.trajectory, storedPoint(datum, pane.controls.state().projection));
  renderTrajectory(pane);
}

/**
 * Add a clicked decoded point to the end of the walk (`V132`, P28).
 *
 * **The projection is taken off the record, not off the controls.** A `DecodedMark` carries the
 * view it was decoded under, and it is only drawn on this pane at all when that view matches what
 * is on screen (`showMarks`) — so reading the record is both correct and one fewer place for the
 * two to disagree.
 */
function appendMarkToTrajectory(pane: Pane, mark: DecodedMark): void {
  pane.trajectory = appendPoint(pane.trajectory, generatedPoint(mark));
  renderTrajectory(pane);
}

/** As `appendMarkToTrajectory`, for the third provenance (`V137`, P29.9). */
function appendEncodedToTrajectory(pane: Pane, mark: EncodedMark): void {
  pane.trajectory = appendPoint(pane.trajectory, encodedPoint(mark));
  renderTrajectory(pane);
}

// -- decoded points that stay on the map (`V123`, P20) -------------------------------------------

/**
 * What makes this pane's coordinates comparable: one projection, of one build, of one atlas.
 *
 * `null` before a manifest has arrived, which is the only state in which a mark cannot be placed
 * or looked up. **`built_at` is in the key and that is not tidiness** — a rebuilt atlas keeps its
 * id and its projection names while refitting the embedding underneath them, so a stored `xy`
 * would name a region of a map that no longer exists. `decoded.ts` drops those records on read.
 */
function viewOf(pane: Pane): DecodedView | null {
  const manifest = pane.manifest;
  if (manifest === null) return null;
  const state = pane.controls.state();
  return { atlasId: state.atlasId, projection: state.projection, builtAt: manifest.built_at };
}

/**
 * Draw one decoded point on this pane's map, replacing whatever was drawn for it.
 *
 * Rebuilt rather than mutated when the card opens or closes: `renderer.mark` replaces the node for
 * an id it already holds, so a fresh element is the whole of the update and there is no partial
 * state to keep in step. The residue delegate is re-attached each time for the same reason
 * `pinSelection` re-attaches — the spans in the new node are new elements.
 *
 * `step` defaults to whatever the walk says about this record, so **every** call site draws a
 * member's card as a member without knowing that the feature exists — the same default-parameter
 * arrangement `pinSelection` uses. `showMarks` and `setMarkCard` are the two that rely on it.
 *
 * ## The diamond is a trajectory click too (`V132`, P28)
 *
 * The reader's request: a decoded point must be addable to the walk by clicking **the diamond
 * itself**. Not by clicking near it — `onEmptyClick`'s `nearestRendered` searches the loaded tiles
 * for a *corpus* row and knows nothing about the decoded overlay, and that asymmetry is deliberate
 * rather than a gap. A decoded point sits at a coordinate no corpus row occupies, so a near-miss
 * that snapped to one would be inventing a target the reader did not aim at.
 *
 * The append comes **before** the pane is written, so the card and the pane already carry the step
 * label rather than being relabelled a moment later. That is the same ordering `attachClicks` uses,
 * and for the same reason.
 */
function drawMark(pane: Pane, mark: DecodedMark, step = stepOfKey(pane, markKey(mark.id))): void {
  // `step i of n` is derived here, from the run's membership as the store currently holds it, and
  // is not on the record — see `InterpolationRun.t`. `undefined` for an ordinary decoded point.
  const at =
    mark.run === undefined ? undefined : runPosition(mark, decodedStore.forRun(mark.run.id));
  const node = decodedMark(
    mark,
    {
      // **The stored response, not a fresh decode.** Re-requesting would sample again under three
      // of the four samplers and put a different sequence under a marker the reader has been
      // looking at. Nothing here touches the network.
      onOpen: () => {
        const mode = pane.controls.state().mode;
        // The diamond is an endpoint click too, for `V132`'s reason: a decoded point sits where no
        // corpus row does, so a near-miss that snapped to one would name a target nobody aimed at.
        if (mode === 'interpolate') selectEndpoint(pane, generatedPoint(mark));
        if (mode === 'trajectory') appendMarkToTrajectory(pane, mark);
        showDecodedPoint(pane, mark.result, mark.xy, mark.id);
        if (!mark.card) setMarkCard(mark, true);
      },
      onDelete: () => removeMark(mark.id),
      onCloseCard: () => setMarkCard(mark, false),
    },
    step,
    at,
    cardNotes(markKey(mark.id), () => {
      for (const other of panes) if (other.marks.has(mark.id)) drawMark(other, mark);
    }),
  );
  pane.marks.get(mark.id)?.detach();
  pane.marks.set(mark.id, { mark, detach: residueCards.attach(node) });
  pane.renderer.mark(mark.id, mark.xy, node);
}

/**
 * Open or close one marker's card, and remember which — so a reload restores what was left.
 *
 * **Applied to every pane holding that mark, not just the one that was clicked.** A mark belongs to
 * a view rather than to a pane, so two panes on the same atlas *and* the same projection draw the
 * same one; updating only the clicked pane would leave the other showing a card the store says is
 * closed until the next view change. Reachable only when the reader points both panes at the same
 * thing — `setComparing` defaults pane B to a different atlas — but the fix is one loop.
 */
function setMarkCard(mark: DecodedMark, open: boolean): void {
  mark.card = open;
  decodedStore.setCard(mark.id, open);
  for (const pane of panes) if (pane.marks.has(mark.id)) drawMark(pane, mark);
  // **The count changed, so the control has to be redrawn here** (`V130`, P26). This is the only
  // place a card opens or closes without a mark being added or removed, and it is reached from
  // four callers — the diamond, the card's own close control, "clear N cards" and Escape. Left
  // out, "clear 1 card" closed the card and then went on saying "clear 1 card" until some other
  // gesture re-rendered it, so it took two clicks to disappear.
  renderMapActions();
}

/**
 * Redraw every decoded point that belongs to what this pane is now showing.
 *
 * **Called after every view change, and it is the counterpart of `clearPin` rather than a copy of
 * it.** A pinned card names a corpus row and is simply dropped when the coordinates stop meaning
 * what they meant; a mark is filtered instead, because the record survives the switch and has to
 * come back when the reader switches back. `forView` is also where a stale build's records are
 * deleted, so this is the one call that keeps the store from accumulating them.
 *
 * The dataset filter is deliberately not applied: `split` is a property of a corpus row and a
 * generated point has none, so hiding marks under "train" would be inventing an answer.
 */
function showMarks(pane: Pane): void {
  clearMarks(pane);
  const view = viewOf(pane);
  // The count is rendered whether or not there was a view to draw: `clearMarks` above has already
  // emptied this pane, so an early return would leave the control naming marks that are gone.
  if (view !== null) for (const mark of decodedStore.forView(view)) drawMark(pane, mark);
  renderMapActions();
  renderInterpolate(pane);
}

/** Record a decode and put it on the map. */
function addMark(pane: Pane, xy: [number, number], result: DecodeResponse): void {
  const view = viewOf(pane);
  if (view === null) return;
  drawMark(pane, decodedStore.add(view, xy, result));
  renderMapActions();
  // The eviction forecast is a count of this view's decoded points, so it moves whenever they do
  // (§5.1). Rendered here rather than inside `renderMapActions` so that the interpolation panel is
  // not rebuilt on every card open and close, which is most of what that function is called for.
  renderInterpolate(pane);
}

/**
 * Forget one point everywhere: every overlay drawing it, its listeners, and the store.
 *
 * Across panes for the same reason `setMarkCard` is, and it matters more here: a diamond left on
 * the second pane after its record was deleted would still open its sequence from the closure,
 * which is a card the store no longer knows about.
 */
function removeMark(id: string): void {
  for (const pane of panes) {
    if (!pane.marks.has(id)) continue;
    pane.marks.get(id)?.detach();
    pane.marks.delete(id);
    pane.renderer.unmark(id);
  }
  decodedStore.remove(id);
  dropFromWalks(new Set([markKey(id)]));
  renderMapActions();
  for (const pane of panes) renderInterpolate(pane);
}

/**
 * Take deleted decoded points out of **every** pane's walk (`V133`).
 *
 * The reader's decision, 2026-08-28, replacing `V132`'s answer: deleting a decoded point removes it
 * from the trajectory rather than leaving an entry marked `gone`. Deletion is a deliberate gesture
 * about that point, and an entry naming a point the reader has just destroyed is not a step of
 * anything.
 *
 * **Stated consequence, since `V132` argued the other way:** the steps after it renumber and, under
 * `'start'`, deleting step 1 re-measures every diff in the walk. That is now the accepted cost of a
 * deliberate deletion. What it is *not* is a cost of anything automatic — `CAP_PER_VIEW`'s eviction
 * does not come through here, so it still leaves the entry in place and `gone`, and no walk
 * renumbers itself because the reader decoded a 201st point somewhere else.
 *
 * **Every pane, not only the ones drawing it.** A record is deleted from the store, which is shared;
 * a pane on another projection can hold an entry naming it, listed as `off-projection`. Filtering to
 * the panes that had drawn it would leave that entry naming a record that no longer exists.
 */
function dropFromWalks(keys: ReadonlySet<string>): void {
  for (const pane of panes) {
    const next = withoutKeys(pane.trajectory, keys);
    if (next === pane.trajectory) continue;
    pane.trajectory = next;
    renderTrajectory(pane);
  }
}

/**
 * Take every marker off this pane's map without forgetting anything.
 *
 * The store is untouched on purpose. This runs on a view change and on a pane teardown, where the
 * records stay valid and only the drawing stops.
 */
function clearMarks(pane: Pane): void {
  for (const { detach } of pane.marks.values()) detach();
  pane.marks.clear();
  pane.renderer.clearMarks();
}

/**
 * Close every decoded point's card, leaving the diamonds (`V129`, P25).
 *
 * **This is what "clear the cards" means for a decoded point, and it is not deletion.** The
 * diamond, the record and the sequence all survive — clicking the diamond brings the card back
 * with the same sequence, because the response was stored and is never re-requested. Deleting is
 * the right-click and the "clear N decoded points" control, and those are the only two.
 *
 * The snapshot matters: `setMarkCard` rebuilds the node, which writes to `pane.marks` mid-loop.
 */
function closeDecodedCards(): void {
  for (const pane of panes) {
    for (const { mark } of [...pane.marks.values()]) {
      if (mark.card) setMarkCard(mark, false);
    }
  }
}

// -- typed sequences: the reader writes them, the model places them (`V137`-`V139`, P29) --------

/**
 * Draw one typed sequence on this pane's map, replacing whatever was drawn for it.
 *
 * `drawMark`'s twin, down to the default `step` — so every call site draws a walk member as a
 * member without knowing the walk exists.
 *
 * **The triangle is a trajectory click too**, exactly as the diamond became one at `V132`: in
 * trajectory mode a click appends this point and reopens it, which is the same "inspect plus an
 * append" a click on a corpus point means. A near-miss cannot add one, and that is not a policy
 * but a fact about the mechanism — `nearestRendered` scans tile record batches, and a typed
 * sequence is in no tile.
 */
function drawEncoded(pane: Pane, mark: EncodedMark, step = stepOfKey(pane, encodedKey(mark.id))): void {
  const node = encodedMark(
    mark,
    {
      onOpen: () => {
        const mode = pane.controls.state().mode;
        // The third provenance is an endpoint too: the sequence is the reader's and only the
        // position is the model's, but the latent behind it is `model.encode`'s and deterministic.
        if (mode === 'interpolate') selectEndpoint(pane, encodedPoint(mark));
        if (mode === 'trajectory') appendEncodedToTrajectory(pane, mark);
        showEncodedPoint(pane, mark);
        if (!mark.card) setEncodedCard(mark, true);
      },
      onDelete: () => removeEncoded(mark.id),
      onCloseCard: () => setEncodedCard(mark, false),
    },
    step,
    cardNotes(encodedKey(mark.id), () => {
      for (const other of panes) if (other.encoded.has(mark.id)) drawEncoded(other, mark);
    }),
  );
  pane.encoded.get(mark.id)?.detach();
  pane.encoded.set(mark.id, { mark, detach: residueCards.attach(node) });
  // **The same overlay the diamonds use**, keyed by an `enc:`-prefixed id. A second overlay would
  // need a third placement call in the renderer's zoom handler for no gain: the nodes are keyed,
  // and the CSS class on the node is what decides which glyph is drawn.
  pane.renderer.mark(encodedKey(mark.id), mark.xy, node);
}

/**
 * Open or close one typed point's card, and remember which — so a reload restores what was left.
 *
 * Across every pane drawing it, for `setMarkCard`'s reason: a point belongs to a view rather than
 * to a pane, so two panes on the same atlas and projection draw the same one.
 */
function setEncodedCard(mark: EncodedMark, open: boolean): void {
  mark.card = open;
  encodedStore.setCard(mark.id, open);
  for (const pane of panes) if (pane.encoded.has(mark.id)) drawEncoded(pane, mark);
  // The card count changed, so the control that names it has to be redrawn here — `V130`'s
  // finding, which cost `#clear-decoded` two clicks to disappear until it was fixed.
  renderMapActions();
}

/**
 * Redraw every typed sequence that belongs to what this pane is now showing.
 *
 * **Where this differs from `showMarks`, and it is the module's whole thesis** (`V139`): a decoded
 * record of a stale build is *deleted* on read, because its coordinate was the input and there is
 * nothing underneath worth keeping. A typed one is **kept and simply not drawn** — the input is the
 * reader's own and survives the rebuild, so what is stale is the coordinate alone. The composer
 * lists those and offers to encode them again.
 *
 * The dataset filter is not applied, for `showMarks`'s reason: `split` is a property of a corpus
 * row and a typed sequence has none.
 */
function showEncoded(pane: Pane): void {
  clearEncodedMarks(pane);
  const view = viewOf(pane);
  if (view !== null) for (const mark of encodedStore.forView(view)) drawEncoded(pane, mark);
  renderMapActions();
  renderEncoder(pane);
}

/**
 * Take every triangle off this pane's map without forgetting anything.
 *
 * `clearMarks`'s twin. **`unmark` per id rather than `clearMarks()`**, because the two provenances
 * share one overlay and the renderer's bulk clear would take the diamonds with them.
 */
function clearEncodedMarks(pane: Pane): void {
  for (const [id, { detach }] of pane.encoded) {
    detach();
    pane.renderer.unmark(encodedKey(id));
  }
  pane.encoded.clear();
}

/**
 * Forget one typed point everywhere: every overlay drawing it, its listeners, and the store.
 *
 * `removeMark`'s twin, including `dropFromWalks`: deletion is a deliberate gesture about that
 * point, and an entry naming a point the reader has just destroyed is not a step of anything
 * (`V133`).
 */
function removeEncoded(id: string): void {
  for (const pane of panes) {
    if (!pane.encoded.has(id)) continue;
    pane.encoded.get(id)?.detach();
    pane.encoded.delete(id);
    pane.renderer.unmark(encodedKey(id));
  }
  encodedStore.remove(id);
  dropFromWalks(new Set([encodedKey(id)]));
  renderMapActions();
  for (const pane of panes) renderEncoder(pane);
}

/**
 * Close every typed point's card, leaving the triangles (`V129`, P25's rule for a third glyph).
 *
 * Closing is not deleting, here as everywhere: the triangle, the record and the sequence survive,
 * and clicking the triangle brings the card back.
 */
function closeEncodedCards(): void {
  for (const pane of panes) {
    for (const { mark } of [...pane.encoded.values()]) {
      if (mark.card) setEncodedCard(mark, false);
    }
  }
}

/**
 * Encode one sequence and put it on the map.
 *
 * **`reconstruct: false`, always** (`V140`). The reconstruction is a decoder run on the `z` this
 * call just computed and it measured 85% of the route; nothing in this app shows a round trip, so
 * asking for one would be paying 269 ms per sequence for a string that is parsed and discarded.
 *
 * Returns whether the point landed, so a bulk import can stop on the first refusal rather than
 * firing the remaining N-1 requests at a server that has already said no.
 */
async function encodeAndPlace(
  pane: Pane,
  sequence: string,
  label: string,
  openCard: boolean,
): Promise<boolean> {
  const view = viewOf(pane);
  const manifest = pane.manifest;
  if (view === null || manifest === null) return false;
  try {
    const result = await encode(view.atlasId, {
      sequence,
      projection: view.projection,
      reconstruct: false,
    });
    // The view is re-read after the await: the reader can change projection while a 200-row import
    // is running, and placing a coordinate computed for the old one is the `V115` failure.
    const now = viewOf(pane);
    if (now === null || now.atlasId !== view.atlasId || now.projection !== view.projection) {
      return false;
    }
    const mark = encodedStore.add(
      view,
      manifest.checkpoint_sha256,
      sequence,
      label,
      result,
      openCard,
    );
    if (mark === null) {
      // **The cap, not `freeSlots`** — which is zero here by definition, and an earlier version of
      // this line printed it: `already holds 0 of its limit`.
      detailError(
        pane,
        `this view already holds its limit of ${CAP_PER_VIEW} typed sequences — remove one to ` +
          'add another. Nothing was placed, and the sequence you typed is still in the box.',
      );
      return false;
    }
    drawEncoded(pane, mark);
    // **The pane shows one result, not each of two hundred.** The composer places one sequence and
    // opening it is the answer to that gesture; a bulk import would otherwise rebuild the detail
    // pane once per row, flickering the whole paste through it and leaving whichever row happened
    // to be last. During an import the progress line is what reports, and the composer's placed
    // list is the record afterwards.
    if (pane.importing === null) showEncodedPoint(pane, mark);
    renderMapActions();
    renderEncoder(pane);
    return true;
  } catch (error) {
    // The server's own message, verbatim. For an out-of-alphabet code that message names the code
    // and says what this checkpoint's alphabet looks like (`server.py:436`), which is the one
    // thing the composer's completion list cannot tell the reader.
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
    return false;
  }
}

/**
 * Place every row of a checked paste, in order, one request at a time.
 *
 * **Sequential and not concurrent**, deliberately. The server holds one model and torch is already
 * multi-threaded, so parallel requests contend rather than compound — and a failure part-way
 * through a concurrent fan-out leaves a set nobody can characterise. Measured with
 * `reconstruct: false`, a 200-row import is ~10 s.
 *
 * **Cancellation is checked between requests, never inside one.** A request that has reached the
 * model will finish and be placed; claiming otherwise would leave the count disagreeing with the
 * map.
 */
async function runImport(pane: Pane, rows: readonly ParsedRow[]): Promise<void> {
  // `first` drives the composer's `loading the projection` line: the first `/encode` against a
  // projection reads its reducer off disk, measured at 30.7 s for a 92 MB `umap.joblib`, and a
  // progress line reading `placing 1 of 74` for half a minute reads as a hang.
  pane.importing = { rows, done: 0, cancelled: false, first: true };
  renderEncoder(pane);
  for (const row of rows) {
    if (pane.importing === null || pane.importing.cancelled) break;
    // **Cards closed for an import.** Five hundred triangles is a dense scatter; five hundred open
    // cards is an unusable page. Each one opens on a click of its own triangle, which is the
    // gesture that asks for it.
    const placed = await encodeAndPlace(pane, row.sequence, row.label, false);
    if (pane.importing === null) return;
    if (!placed) break;
    pane.importing = { ...pane.importing, done: pane.importing.done + 1, first: false };
    renderEncoder(pane);
  }
  const done = pane.importing?.done ?? 0;
  pane.importing = null;
  if (done < rows.length) {
    detailError(pane, `placed ${done} of ${rows.length} — the rest were not attempted`);
  }
  renderEncoder(pane);
}

/**
 * Encode the stale records again, so they can be drawn on the build that is loaded (`V139`).
 *
 * **The same path a bulk import takes**, because it is the same operation: N sequences, in order,
 * with a progress line and a cancel. `encodedStore.add` keys on `(view, sequence)`, so each one
 * *replaces* its stale record rather than adding a second — the reader ends with the points they
 * had, at the coordinates this build assigns them.
 */
function rePlaceStale(pane: Pane): void {
  const view = viewOf(pane);
  if (view === null) return;
  const rows = encodedStore
    .staleForView(view)
    .map((mark, at) => ({ line: at + 1, sequence: mark.sequence, label: mark.label }));
  if (rows.length === 0) return;
  // The stale records are removed only once their replacements land, and `add` does that itself by
  // id. Removing them up front would lose the sequences if the first request failed.
  void runImport(pane, rows);
}

function encoderHandlers(pane: Pane): EncoderHandlers {
  return {
    onPlace: (sequence, label) => void encodeAndPlace(pane, sequence, label, true),
    onImport: (rows) => void runImport(pane, rows),
    onCancel: () => {
      if (pane.importing !== null) pane.importing.cancelled = true;
  // A run outlives its own `await` for the same reason an import does.
  if (pane.interpolation.running !== null) pane.interpolation.running.cancelled = true;
    },
    onRePlace: () => rePlaceStale(pane),
    onSelect: (id) => {
      const held = pane.encoded.get(id);
      if (held !== undefined) showEncodedPoint(pane, held.mark);
    },
    onRemove: (id) => removeEncoded(id),
  };
}

/**
 * Draw the composer, or take it away.
 *
 * **Absent on a non-peptide atlas** (`V95`, P29.5). `server.py` refuses `/encode` on the four
 * residue and chemistry atlases with a 409, because a decoder run over one of those rows returns a
 * sequence that means nothing. The decoder *tool* is still offered there and explains itself
 * through that refusal, which is tolerable for one option in a dropdown; a whole composer would be
 * inviting someone to type a peptide that can never be placed.
 */
function renderEncoder(pane: Pane): void {
  const manifest = pane.manifest;
  const view = viewOf(pane);
  if (manifest === null || view === null || !isPeptideAtlas(manifest)) {
    pane.encoderPanel.clear();
    return;
  }
  ensureResidueCodes();
  pane.encoderPanel.show(
    {
      codes: residueCodeList,
      trainedLength: manifest.max_length,
      placed: [...pane.encoded.values()].map((held) => held.mark),
      stale: encodedStore.staleForView(view),
      freeSlots: encodedStore.freeSlots(view),
      progress:
        pane.importing === null
          ? null
          : {
              done: pane.importing.done,
              total: pane.importing.rows.length,
              loadingProjection: pane.importing.first,
            },
    },
    encoderHandlers(pane),
  );
}

// -- the latent line between two points (`V143`-`V146`, P30) -------------------------------------

/**
 * Which projection a point's coordinates belong to, whatever its provenance.
 *
 * One function because the answer is stored in three places — the entry's own `projection` for a
 * corpus row, the record's for the other two — and three call sites asking it three ways is three
 * chances to compare the wrong pair.
 */
const projectionOf = (point: TrajectoryPoint): string =>
  point.kind === 'stored' ? point.projection : point.mark.projection;

/**
 * One endpoint slot, with the sentence saying why it cannot be used, or `null` (`V143`).
 *
 * Two refusals, and they are different failures:
 *
 * - **another projection** — `V115`, and it is a **blanket** rule rather than a per-reference one
 *   by the reader's decision at `V148`. Some references would survive a switch on their own terms:
 *   a corpus row is a memmap read and a typed sequence is an encode, neither of which involves a
 *   projection. The rule refuses them anyway, so that every point named in this panel belongs to
 *   the projection on screen and no surface has to reason about which kind of reference it holds.
 *   The trajectory panel calls the same fact `off-projection`.
 *
 *   The cost is stated rather than hidden: an endpoint picked before a projection change is
 *   refused until it is picked again, and `clear both` is the one gesture that empties the pair.
 * - **no recipe** — a decoded record whose `fill` is `z` and which carries no `run`. That is an
 *   interpolated point placed before this phase existed: its latent was computed and nothing about
 *   it was kept, so there is no reference that names it. A live record cannot reach this state,
 *   which is why the sentence says what it does rather than offering a repair.
 */
function endpointOf(pane: Pane, point: TrajectoryPoint): Endpoint {
  const state = pane.controls.state();
  if (projectionOf(point) !== state.projection) {
    return { point, refusal: 'captured under another projection' };
  }
  if (latentRefOf(point) === null) {
    return { point, refusal: 'an older interpolated point, with no recipe to rebuild its latent' };
  }
  return { point, refusal: null };
}

/**
 * A click in interpolate mode: fill a slot, or empty the one this point is already in.
 *
 * **First click fills `start`, second fills `end`, third replaces `end`.** Replacing the *end* is
 * the useful default: the reader is exploring outward from a fixed point, and a third click that
 * shifted both slots along would silently discard the endpoint they chose first.
 *
 * **Clicking a point that is already an endpoint clears that slot**, which is the only way to undo
 * a mis-click without reaching for the panel — and the panel offers the same thing per slot.
 *
 * A run already on screen is left alone: its points stay on the map and in the store. Only the
 * panel's summary is dropped, because it describes a walk between two points that are no longer
 * both selected.
 */
function selectEndpoint(pane: Pane, point: TrajectoryPoint): void {
  const held = pane.interpolation;
  const key = keyOf(point);
  if (held.start !== null && keyOf(held.start) === key) {
    pane.interpolation = { ...held, start: held.end, end: null, result: null, placed: [] };
  } else if (held.end !== null && keyOf(held.end) === key) {
    pane.interpolation = { ...held, end: null, result: null, placed: [] };
  } else if (held.start === null) {
    pane.interpolation = { ...held, start: point, result: null, placed: [] };
  } else {
    pane.interpolation = { ...held, end: point, result: null, placed: [] };
  }
  renderInterpolate(pane);
}

/**
 * Run the walk and put its unique steps on the map (`V143`, P30.11).
 *
 * **One request for the whole walk**, which is the shape the measurements chose: a batched decode
 * of twelve latents is 9.8x cheaper than twelve calls, one `transform` pays `umap-learn`'s ~4.5 s
 * first-call compilation once instead of `steps + 2` times, and a run that failed half way through
 * would leave a set nobody could characterise.
 *
 * **The view is re-read after the `await`** and the answer dropped if it moved, exactly as
 * `encodeAndPlace` does: the reader can change projection while a first UMAP run is loading its
 * reducer, and placing coordinates computed for the old one is the `V115` failure.
 */
async function runInterpolation(pane: Pane): Promise<void> {
  const held = pane.interpolation;
  const view = viewOf(pane);
  const manifest = pane.manifest;
  if (held.start === null || held.end === null || view === null || manifest === null) return;
  const startRef = latentRefOf(held.start);
  const endRef = latentRefOf(held.end);
  if (startRef === null || endRef === null) return;

  const state = pane.controls.state();
  // `loadingProjection` drives the composer's own sentence, reused verbatim: the first `/interpolate`
  // against a UMAP projection reads its reducer (measured 11.0 s for `ancestor_10m_kl003`) and then
  // pays ~4.5 s of numba compilation inside `transform`, and a bare "decoding…" for fifteen seconds
  // reads as a hang.
  pane.interpolation = {
    ...held,
    running: { cancelled: false, loadingProjection: specOf(pane)?.kind === 'umap' },
    result: null,
    placed: [],
  };
  renderInterpolate(pane);
  try {
    const answer = await interpolate(view.atlasId, {
      start: startRef,
      end: endRef,
      projection: view.projection,
      steps: held.steps,
      sampler: state.sampler,
      sampler_params: state.samplerParams,
    });
    const running = pane.interpolation.running;
    if (running === null || running.cancelled) return;
    const now = viewOf(pane);
    if (now === null || now.atlasId !== view.atlasId || now.projection !== view.projection) {
      pane.interpolation = { ...pane.interpolation, running: null };
      renderInterpolate(pane);
      return;
    }
    pane.interpolation = {
      ...pane.interpolation,
      running: null,
      result: answer.steps,
      refs: { start: startRef, end: endRef, sampler: answer.sampler },
    };
    placeRun(pane, view, manifest, originOf(held.start), originOf(held.end));
    renderMapActions();
  } catch (error) {
    pane.interpolation = { ...pane.interpolation, running: null };
    // The server's own message. For a recipe from another checkpoint it names both shas and says
    // which of the two things moved, which is the one thing the client cannot work out for itself.
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
  } finally {
    if (pane.interpolation.running !== null) {
      pane.interpolation = { ...pane.interpolation, running: null };
    }
    renderInterpolate(pane);
  }
}

/**
 * Put this pane's last answer on the map, replacing whatever the previous placement left there.
 *
 * **Called by the run and by the endpoint-match switch, and that is the reason it is a function.**
 * The switch decides whether a step that only reproduces an endpoint is placed; answering it by
 * re-requesting would be a second identical round trip for a decision the answer already contains,
 * and answering it only on the *next* run would be a control that appears to do nothing.
 *
 * Whatever the previous placement wrote is removed first — by run id, so a step that is no longer
 * placed does not survive as an orphan diamond nobody can account for.
 */
function placeRun(
  pane: Pane,
  view: DecodedView,
  manifest: AtlasManifest,
  from: string,
  to: string,
): void {
  const held = pane.interpolation;
  const answer = held.result;
  const refs = held.refs;
  if (answer === null || refs === null) return;

  const wanted = placeable(answer, held.includeEndpointMatches);
  const runId = held.runId ?? newRunId();
  const already = new Map(held.placed.map((one) => [one.step.index, one.markId]));

  // **Only the difference is written**, which is what makes the endpoint-match switch a safe
  // control rather than a destructive one. Forgetting the whole run and re-adding it would mint a
  // new id for every surviving point — so any walk holding one would lose the entry (`V133` drops
  // entries naming destroyed records), and every open card would close. The steps that stay,
  // stay.
  const keep = new Set(wanted.map((step) => step.index));
  const dropped = held.placed
    .filter((one) => !keep.has(one.step.index) && one.markId !== null)
    .map((one) => one.markId as string);
  if (dropped.length > 0) forgetMarks(dropped);

  const placed: PlacedStep[] = wanted.map((step) => {
    const existing = already.get(step.index);
    if (existing !== undefined && existing !== null) return { step, markId: existing };
    const run: InterpolationRun = {
      id: runId,
      t: step.t,
      tRange: spanOf(answer, step),
      path: 'linear',
      // **The recipe, not the vector** (`V144`). `flatten` merges the two endpoints' own leaves at
      // this `t`, so a point of this run can itself be an endpoint of the next one without 256
      // floats being kept anywhere.
      recipe: flatten(refs.start, refs.end, step.t),
      checkpointSha: manifest.checkpoint_sha256,
      from,
      to,
    };
    const mark = decodedStore.add(
      view,
      step.xy,
      // **Shaped exactly as `POST /decode` with a `z` would answer**, because that is what
      // happened: the vector was given rather than invented from a coordinate, so `fill` is `z`,
      // `exact` is true and no row was consulted.
      {
        sequence: step.sequence,
        fill: 'z',
        sampler: refs.sampler,
        exact: true,
        neighbour_row: null,
        z_norm: step.z_norm,
      },
      run,
    );
    return { step, markId: mark.id };
  });
  pane.interpolation = { ...pane.interpolation, placed, runId };
  // **Every point of the run is redrawn, not only the new ones**: `step i of n` is derived from the
  // run's membership, so adding or removing one renumbers the rest.
  for (const one of placed) {
    const mark = decodedStore.forRun(runId).find((m) => m.id === one.markId);
    if (mark !== undefined) drawMark(pane, mark);
  }
}

/** Take these records off every map and out of the store and the walks. */
function forgetMarks(ids: readonly string[]): void {
  if (ids.length === 0) return;
  decodedStore.removeMany(ids);
  for (const other of panes) {
    for (const id of ids) {
      if (!other.marks.has(id)) continue;
      other.marks.get(id)?.detach();
      other.marks.delete(id);
      other.renderer.unmark(id);
    }
  }
  // Deliberate deletion takes the entries out of every walk, which is `V133`'s rule: an entry
  // naming a destroyed point is not a step of anything.
  dropFromWalks(new Set(ids.map((id) => markKey(id))));
}

/** Take a whole run's records off every map and out of the store and the walks. */
function forgetRun(runId: string): void {
  forgetMarks(decodedStore.forRun(runId).map((mark) => mark.id));
}

/**
 * Forget one run: its points leave the map, the store, and any walk holding them (P30.13).
 *
 * **`forRun` rather than the panel's own list**, so a run placed into a view the reader has since
 * left is removed whole rather than leaving the half that happens to be on screen.
 */
function clearRun(pane: Pane): void {
  const runId = pane.interpolation.runId;
  if (runId === null) return;
  forgetRun(runId);
  pane.interpolation = {
    ...pane.interpolation,
    result: null,
    placed: [],
    runId: null,
    refs: null,
  };
  for (const other of panes) renderInterpolate(other);
  renderMapActions();
}

function interpolateHandlers(pane: Pane): InterpolateHandlers {
  return {
    onSteps: (count) => {
      pane.interpolation = { ...pane.interpolation, steps: count };
      renderInterpolate(pane);
    },
    onSwap: () => {
      const held = pane.interpolation;
      pane.interpolation = { ...held, start: held.end, end: held.start, result: null, placed: [] };
      renderInterpolate(pane);
    },
    onClearEndpoint: (which) => {
      const held = pane.interpolation;
      pane.interpolation =
        which === 'start'
          ? { ...held, start: null, result: null, placed: [] }
          : { ...held, end: null, result: null, placed: [] };
      renderInterpolate(pane);
    },
    // **The same rule as one slot, applied to both** (`V148`): the selection is emptied and the
    // summary with it, because it describes a walk between two points that are no longer chosen.
    // The run's points stay on the map — `onClearRun` is the control that removes those, and a
    // gesture that quietly did both would destroy records the reader only asked to deselect.
    onClearEndpoints: () => {
      pane.interpolation = {
        ...pane.interpolation,
        start: null,
        end: null,
        result: null,
        placed: [],
      };
      renderInterpolate(pane);
    },
    onRun: () => void runInterpolation(pane),
    onCancel: () => {
      // **Cleared here rather than only flagged.** The request cannot be recalled — it has reached
      // the model — but the reader asked to stop waiting, and `runInterpolation` re-reads this
      // after its `await` and drops the answer when it is gone. Flagging alone would leave
      // `decoding…` on screen for the fifteen seconds a cold UMAP run takes.
      if (pane.interpolation.running === null) return;
      pane.interpolation.running.cancelled = true;
      pane.interpolation = { ...pane.interpolation, running: null };
      renderInterpolate(pane);
    },
    onSelect: (markId) => {
      const held = pane.marks.get(markId);
      if (held !== undefined) showDecodedPoint(pane, held.mark.result, held.mark.xy, held.mark.id);
    },
    onClearRun: () => clearRun(pane),
    onIncludeEndpointMatches: (include) => {
      pane.interpolation = { ...pane.interpolation, includeEndpointMatches: include };
      const view = viewOf(pane);
      const manifest = pane.manifest;
      const held = pane.interpolation;
      // No request: the answer already says which steps those are.
      if (view !== null && manifest !== null && held.result !== null && held.start !== null && held.end !== null) {
        placeRun(pane, view, manifest, originOf(held.start), originOf(held.end));
        renderMapActions();
      }
      renderInterpolate(pane);
    },
  };
}

/**
 * Draw the interpolation panel, or take it away.
 *
 * **Absent on a non-peptide atlas**, for `renderEncoder`'s reason: `server.py` refuses this route
 * with a 409 on the four residue and chemistry atlases, and a whole panel inviting someone to pick
 * two monomers and walk between them would be inviting a refusal.
 */
function renderInterpolate(pane: Pane): void {
  const manifest = pane.manifest;
  const view = viewOf(pane);
  if (manifest === null || view === null || !isPeptideAtlas(manifest)) {
    pane.interpolatePanel.clear();
    return;
  }
  const held = pane.interpolation;
  const state = pane.controls.state();
  const running = held.running;
  pane.interpolatePanel.show(
    {
      start: held.start === null ? null : endpointOf(pane, held.start),
      end: held.end === null ? null : endpointOf(pane, held.end),
      steps: held.steps,
      sampler: state.sampler,
      projectionKind: specOf(pane)?.kind ?? 'pca',
      includeEndpointMatches: held.includeEndpointMatches,
      // Counted from the store rather than from `pane.marks`, because the cap is per view and the
      // store is what enforces it — `pane.marks` is only what this pane has drawn.
      forecast: evictionForecast(decodedStore.forView(view).length, held.steps),
      running: running === null ? null : { loadingProjection: running.loadingProjection },
      result: held.result,
      placed: held.placed,
      active: state.mode === 'interpolate',
    },
    interpolateHandlers(pane),
  );
}

/**
 * How many cards are covering the maps right now — pinned, decoded and typed together (`V129`).
 *
 * **All three, because a card is a card whoever wrote its sequence.** This number is what
 * `clear N cards` acts on, and that control removes clutter from the map rather than making a
 * claim about provenance; leaving one class out would be a button that says it clears the cards
 * and leaves some standing.
 */
function openCards(): number {
  const open = (held: Iterable<{ mark: { card: boolean } }>): number =>
    [...held].filter((one) => one.mark.card).length;
  return panes.reduce(
    (total, pane) => total + pane.pins.size + open(pane.marks.values()) + open(pane.encoded.values()),
    0,
  );
}

/**
 * Show or hide "clear all", and tell the stylesheet whether anything is pinned (`V116`, `P19.10`).
 *
 * **One button each for the page, not one per pane.** They are a way out of a map covered in
 * cards, and a reader who has filled both panes wants one gesture rather than two.
 *
 * **`body.has-map-actions` replaces `body.has-pins`, and that is a simplification rather than a
 * rename** (P20.7). Both buttons and the eviction notice live at the top-left corner and none of
 * them can be told about the others except through a class on `body`. With two absolutely
 * positioned controls there the old one-way rule would have had to become a matrix; `#map-actions`
 * is a flex column, so the notice needs to know only whether that column has anything in it.
 *
 * **The two controls count different things, and the distinction is the point** (`V129`). One
 * counts **cards covering the map** — pinned and decoded together, because that is the clutter the
 * control removes and a card is a card whoever wrote its sequence. The other counts **decoded
 * points**, which is a claim about the model rather than about the screen, and it is the only one
 * of the two that destroys anything.
 *
 * This revises `V123`'s rule that the two counts are never added. That rule was about not summing
 * *pinned rows* with *generated points*, which really would be a summary this app must not offer.
 * Cards on screen is a different quantity, and it is exactly what its button acts on.
 */
function renderMapActions(): void {
  const cards = openCards();
  const marked = panes.reduce((total, pane) => total + pane.marks.size, 0);
  // **Counted separately from `marked`, and that separation is the point** (`V129`, extended at
  // P29). "N decoded points" is a claim about what the *model wrote*; "N typed sequences" is a
  // claim about what the *reader wrote*. One combined total would be the summary this app must not
  // offer — it would say how many points are not corpus members without saying which of two very
  // different things each one is.
  const typed = panes.reduce((total, pane) => total + pane.encoded.size, 0);
  document.body.classList.toggle('has-map-actions', cards + marked + typed > 0);

  if (clearPinsButton !== null) {
    clearPinsButton.hidden = cards === 0;
    clearPinsButton.textContent = `clear ${cards} ${cards === 1 ? 'card' : 'cards'}`;
  }
  if (marked === 0) disarmClearDecoded();
  if (clearDecodedButton !== null) {
    const armed = clearDecodedArmed !== null;
    clearDecodedButton.hidden = marked === 0;
    clearDecodedButton.classList.toggle('armed', armed);
    clearDecodedButton.textContent = armed
      ? `click again to clear ${marked}`
      : `clear ${marked} decoded ${marked === 1 ? 'point' : 'points'}`;
  }
  if (typed === 0) disarmClearEncoded();
  if (clearEncodedButton !== null) {
    const armed = clearEncodedArmed !== null;
    clearEncodedButton.hidden = typed === 0;
    clearEncodedButton.classList.toggle('armed', armed);
    // **"encoded", not "typed"** — the reader's word for what this panel places, and the same word
    // the panel's own heading now uses. The verb differs from its two neighbours for the same
    // reason: this one destroys records, while `clear N cards` only closes them.
    clearEncodedButton.textContent = armed
      ? `click again to erase ${typed}`
      : `erase ${typed} encoded ${typed === 1 ? 'sequence' : 'sequences'}`;
  }
}

async function reconstruct(pane: Pane, datum: PointDatum): Promise<void> {
  const state = pane.controls.state();
  // **Its step and its marks survive asking for a reconstruction** (`V136`). This re-drew the
  // selection with no step at all, so a member's card in the panel lost its place in the walk the
  // moment the reader pressed the button beneath it.
  showSelected(pane, datum);
  try {
    // `row`, not `ix` (`V51`), and no fill: the vector is *read* rather than invented, which is
    // why the response comes back with `fill: "row"`.
    const result = await decode(state.atlasId, {
      row: datum.row,
      sampler: state.sampler,
      sampler_params: state.samplerParams,
    });
    showSelected(pane, datum, result);
  } catch (error) {
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
  }
}

async function decodeAt(pane: Pane, xy: [number, number]): Promise<void> {
  const state = pane.controls.state();
  detailBusy(pane, `decoding (${xy[0].toFixed(4)}, ${xy[1].toFixed(4)})…`);
  try {
    const result = await decode(state.atlasId, {
      projection: state.projection,
      xy,
      fill: state.fill,
      sampler: state.sampler,
      sampler_params: state.samplerParams,
    });
    // `null`: a decode is not in any walk, and the diamond's own click is what would add it.
    showDecodedPoint(pane, result, xy, null);
    // **The point stays on the map, and only on success** (`V123`, P20.4). A refusal below has
    // nothing to record: no sequence was generated, so there is nothing a marker could stand for.
    addMark(pane, xy, result);
  } catch (error) {
    // §3.4's refusal arrives here as a 400 naming the fills that *are* valid. The UI greys the
    // option out; this is what happens when the UI has a bug, which is the case §0.5 exists for.
    detailError(pane, error instanceof ApiError ? error.detail : String(error));
  }
}

// -- the global bar: comparison, linking, and the model switcher --------------------------------

/**
 * Re-render everything that depends on the listing: the global bar, then each pane's controls and
 * status line. One function so that a resident marker cannot be stale in one place and fresh in
 * another.
 */
function renderPanel(): void {
  if (listing === null) return;
  renderGlobal(listing);
  for (const pane of panes) {
    if (pane.manifest === null) continue;
    pane.controls.render(listing, pane.manifest);
    renderStatus(pane);
  }
  // The URL is the view: atlas, projection (a raw pair included) and the comparison, so a link
  // reproduces what is on screen (P11.3's DoD).
  writeUrl();
}

function writeUrl(): void {
  const first = panes[0];
  if (first === undefined || first.manifest === null) return;
  const state = first.controls.state();
  const params = new URLSearchParams({ atlas: state.atlasId, projection: state.projection });
  const second = paneOf('-b');
  if (second !== undefined) params.set('compare', second.controls.state().atlasId);
  history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
}

/**
 * The comparison switch, the channel link, and the model switcher (P12.1, P12.3).
 *
 * **The switcher is manifests only, and that is §4's rule rather than a rendering choice.**
 * `GET /atlases` reads one JSON per directory and maps nothing; listing must not be `O(disk)`, so
 * everything shown here is a field the listing already carried. **IVELS's Level 1 metrics table is
 * explicitly not built** (`plan §0`) — this names models, it does not score them.
 */
function renderGlobal(current: AtlasListing): void {
  globalRoot.replaceChildren();

  const compare = document.createElement('input');
  compare.type = 'checkbox';
  compare.id = 'compare-toggle';
  compare.checked = comparing;
  compare.disabled = current.atlases.length < 2;
  compare.addEventListener('change', () => void setComparing(compare.checked));

  const link = document.createElement('input');
  link.type = 'checkbox';
  link.id = 'link-toggle';
  link.checked = linked;
  link.disabled = !comparing;
  link.addEventListener('change', () => {
    linked = link.checked;
  });

  const compareLabel = document.createElement('label');
  compareLabel.append(compare, ' compare two atlases side by side');
  const linkLabel = document.createElement('label');
  linkLabel.append(link, ' apply colour, landmarks, dataset and curated sets to both');

  const switcher = document.createElement('ul');
  switcher.id = 'switcher';
  for (const m of current.atlases) {
    const item = document.createElement('li');
    item.className = current.resident.includes(m.atlas_id) ? 'atlas resident' : 'atlas';
    item.dataset['atlas'] = m.atlas_id;
    const name = document.createElement('strong');
    name.textContent = m.atlas_id;
    const facts = document.createElement('span');
    facts.className = 'facts';
    const rows =
      m.n_rows < m.n_corpus_rows
        ? `${m.n_rows.toLocaleString()} of ${m.n_corpus_rows.toLocaleString()} rows`
        : `${m.n_rows.toLocaleString()} rows`;
    facts.textContent =
      `${rows} · ${m.latent_dim} dims · ` +
      `${m.projections.map((p) => p.name).join(', ') || 'no projections'} · built ${m.built_at.slice(0, 10)}`;
    const mark = document.createElement('span');
    mark.className = 'residency';
    mark.textContent = current.resident.includes(m.atlas_id) ? 'resident' : 'on disk';
    item.append(name, mark, facts);
    switcher.append(item);
  }

  const caption = document.createElement('p');
  caption.className = 'help';
  caption.textContent = `${current.resident.length} of ${current.capacity} slots resident (V17)`;

  globalRoot.append(compareLabel, linkLabel, switcher, caption);
}

/**
 * Turn the second pane on or off (P12.1).
 *
 * **Both panes are rebuilt, not resized.** deepscatter takes its canvas size once, at
 * construction, and installs no `ResizeObserver`; a pane whose element halved would keep drawing
 * at the old width, and its `color_pick` hit test — which is in canvas pixels — would disagree
 * with what the reader sees by exactly the overflow. Rebuilding costs a re-parse of tiles the
 * browser has already cached and is the only way to be right.
 */
async function setComparing(on: boolean): Promise<void> {
  if (on === comparing) return;
  comparing = on;
  document.body.classList.toggle('comparing', comparing);
  await rebuildPanes();
  renderPanel();
}

/**
 * Destroy and remake every pane, preserving its control state (P12.1, `V114`).
 *
 * Split out of `setComparing` at `P19.4`: turning the comparison on and *resizing the panel* need
 * exactly the same thing done for exactly the same reason — a deepscatter canvas is sized once,
 * at construction, and a pane whose element changed width would keep drawing at the old one with
 * a hit test to match. Nothing about this is specific to the comparison switch.
 */
async function rebuildPanes(): Promise<void> {
  const firstState = paneOf('')?.controls.state();
  const secondState = paneOf('-b')?.controls.state();
  // **The walk is preserved across a rebuild, like the control state beside it** (`V131`). A pane
  // is destroyed and remade because a deepscatter canvas is sized once; neither the comparison
  // switch nor a panel resize is a statement about which points the reader chose.
  const firstWalk = paneOf('')?.trajectory ?? [];
  const firstBaseline = paneOf('')?.baseline ?? 'start';
  const secondWalk = paneOf('-b')?.trajectory ?? [];
  const secondBaseline = paneOf('-b')?.baseline ?? 'start';
  for (const pane of [...panes]) destroyPane(pane);

  const rebuilt = createPane('');
  if (firstState !== undefined) rebuilt.controls.set(firstState);
  rebuilt.trajectory = firstWalk;
  rebuilt.baseline = firstBaseline;
  await showAtlas(rebuilt);

  if (comparing && listing !== null) {
    const other = createPane('-b');
    other.trajectory = secondWalk;
    other.baseline = secondBaseline;
    const fallback =
      listing.atlases.find((m) => m.atlas_id !== firstState?.atlasId) ?? listing.atlases[0];
    other.controls.set(secondState ?? { atlasId: fallback?.atlas_id ?? '', projection: '' });
    // **Sequentially, not concurrently.** Two `POST /load` in flight against a two-slot registry
    // can evict each other, and the notice would then name an atlas the viewer is looking at
    // (`V17`, §4). One at a time is also the order the eviction rule is defined in.
    await showAtlas(other);
  }
}

// -- boot --------------------------------------------------------------------------------------

async function boot(): Promise<void> {
  listing = await listAtlases();
  // **Only the comparison layout is rebuilt on release** (`V114`). With one pane `#maps` is
  // `inset: 0` whatever the panel does, so the map's size — and therefore its hit test — never
  // changed and there is nothing to remake.
  if (resizer !== null && !resizerAttached) {
    createPanelResizer(resizer, panel, () => {
      if (comparing) void rebuildPanes().then(renderPanel);
    });
    resizerAttached = true;
  }
  gate.hidden = true;
  // Before the first `showAtlas`, so the controls render with the group already in them rather
  // than gaining three rows a moment later. Awaited for that reason and no other — it cannot fail
  // in a way the caller acts on.
  await loadCuratedSets();
  const first = listing.atlases[0];
  if (first === undefined) {
    statusbar.textContent = 'no atlases on this server';
    return;
  }
  const flags = first.columns.filter((c) => c.role === 'boolean');
  const params = new URLSearchParams(location.search);
  const pane = paneOf('') ?? createPane('');
  pane.controls.set({
    // **Landmarks are on by default, and that is what P10.2 is for**: eight known peptides on a
    // 100,000-point map is what makes it legible on first open, and a legibility aid nobody
    // switches on is a legibility aid nobody sees.
    landmarks: flags[0]?.name ?? null,
    atlasId: params.get('atlas') ?? first.atlas_id,
    projection: params.get('projection') ?? '',
  });
  await showAtlas(pane);
  const compare = params.get('compare');
  if (compare !== null && listing.atlases.some((m) => m.atlas_id === compare)) {
    await setComparing(true);
    const other = paneOf('-b');
    if (other !== undefined && other.controls.state().atlasId !== compare) {
      other.controls.set({ atlasId: compare });
      await showAtlas(other);
      renderPanel();
    }
  }
}

// **Escape dismisses the map cards too.** `residues.ts` already unpins the structure card on
// Escape; a reader who has pinned both should not have to find two different gestures.
//
// **It closes the decoded and typed cards and still deletes neither point** (`V123`, `V129`,
// extended at P29). Escape is the transient-dismissal gesture, and everything it clears can be
// brought back without a new decision: a pinned card names a corpus row, the structure card is a
// fetch away, a decoded card reopens from the stored response with the same sequence, and a typed
// card reopens from the sequence the reader wrote. What Escape must never do is destroy the
// *record* — for a decoded point because the sampler is unseeded and it cannot be regenerated, for
// a typed one because the sequence is the reader's own work and no click brings it back.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  // The map cards first, then the pinned ones, for the reason `#clear-pins` gives: `clearPin` is
  // what re-renders the trajectory panel, and it has to run after every status has changed.
  closeDecodedCards();
  closeEncodedCards();
  for (const pane of panes) clearPin(pane);
});

function showLoginGate(message: string): void {
  gate.hidden = false;
  const form = gate.querySelector('form') as HTMLFormElement;
  const field = gate.querySelector('input') as HTMLInputElement;
  const error = gate.querySelector('.error') as HTMLElement;
  error.textContent = message;
  form.onsubmit = (event) => {
    event.preventDefault();
    void (async () => {
      try {
        await login(field.value);
        field.value = '';
        await boot();
      } catch (failure) {
        error.textContent = failure instanceof ApiError ? failure.detail : String(failure);
      }
    })();
  };
}

void (async () => {
  try {
    await boot();
  } catch (error) {
    if (error instanceof Unauthenticated) showLoginGate('');
    else {
      const status = paneOf('')?.status ?? statusbar;
      status.textContent = error instanceof ApiError ? error.detail : String(error);
    }
  }
})();

/**
 * A read-only handle on the app's own objects, for the browser test.
 *
 * ES module exports are not reachable from `page.evaluate`, so a test that wanted to assert on
 * *the datum the renderer produced* — rather than on the text the pane happened to render — had no
 * way to get at it. The alternative is a test that re-implements the click path, which would then
 * pass while the app was broken. Nothing here is a secret and nothing is settable: it is the same
 * objects the module already works with.
 *
 * `renderer`, `controls` and `detail` name **pane A's**, which is what they meant before a second
 * pane existed; `panes` is the whole list. They are getters rather than fields because a pane is
 * rebuilt when the comparison is switched on, and a captured reference would be to a destroyed
 * renderer (P12.1).
 */
declare global {
  interface Window {
    peptideVis?: {
      readonly renderer: LatentRenderer | undefined;
      readonly controls: Controls | undefined;
      readonly detail: Detail | undefined;
      readonly panes: readonly Pane[];
      readonly decoded: typeof decodedStore;
      readonly encoded: typeof encodedStore;
      readonly rawProjectionName: typeof rawProjectionName;
    };
  }
}
window.peptideVis = {
  get renderer() {
    return paneOf('')?.renderer;
  },
  get controls() {
    return paneOf('')?.controls;
  },
  get detail() {
    return paneOf('')?.detail;
  },
  get panes() {
    return panes;
  },
  // Exposed for the same reason the renderer is: a test that re-implemented the storage key and
  // the record shape would pass while the app was broken.
  decoded: decodedStore,
  encoded: encodedStore,
  rawProjectionName,
};
